import { useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ko';
import DateNavigator, { PastDateBanner } from './DateNavigator';
import {
    isFirstPass,
    needsClassification,
    watchReasons,
    WATCH_REASON_TEXT,
    STABLE_RULE,
} from './passOrder';
import { useOpsContext } from './useOpsContext';
import {
    useOpsMembersQuery,
    useSetOpsMemberHiddenMutation,
} from '../../services/useOpsBriefingService';
import { useOrderConfirm } from './OrderConfirmModal';
import { useGiftSendFlow } from './useGiftSendFlow';
import {
    toMemberViews,
    MemberView,
    OrderView,
    sumPrepaid,
    sumExpected,
    pickPrimaryCards,
} from './adapters';
import { summarizeRollup, ROLLUP_PARTIAL_NOTE } from './memberRollup';
import { sortMembersForStage } from './memberSort';
import { OpsLoading, OpsError, OpsEmpty, OpsMockBadge } from './OpsStates';
import { isOpsBriefingMock } from '../../api/opsBriefing';
import { STAGE_META, STAGE_GUIDE, MemberStage, won, shortWon } from './tabData';
import {
    VALUE_UNAVAILABLE_TEXT,
    VALUE_UNAVAILABLE_REASON,
    valueUnavailableReason,
    FIRST_PASS_REREGISTRATION_RATE,
} from './memberValue';
import {
    isSessionless,
    SESSIONLESS_REMAIN_TEXT,
    SESSIONLESS_REMAIN_HINT,
} from './sessionless';
import MemberSummaryRow, {
    MemberSummaryHeader,
    SummaryCapabilities,
    summaryMinWidth,
} from './MemberSummaryRow';
import MemberDetailDrawer from './MemberDetailDrawer';
import {
    isPaceAlert,
    isRemainAlert,
    isExpiryAlert,
    isGapAlert,
    isSameDayAdjustAlert,
    SAME_DAY_ADJUST_ALERT,
} from './memberSignals';
import { weeklyPaceText, paceTooltip } from './pace';
import ValueBreakdownModal, { ValueTrigger } from './ValueBreakdownModal';
import {
    memberPrepaidRows,
    memberExpectedRows,
    countUnpricedCards,
    PREPAID_DEFINITION,
    EXPECTED_DEFINITION,
    PREPAID_NOTES,
    expectedNotes,
    UNPRICED_REASON,
} from './valueBreakdown';

dayjs.locale('ko');

/**
 * 회원 현황 — 한 번에 한 단계만.
 *
 * 이전 버전은 4단계를 칸반으로 나란히 놓았다. 정보는 다 있었지만
 * 12명치 카드가 동시에 눈에 들어와서 "그래서 뭘 봐야 하지"가 남았다.
 *
 * 그래서 뒤집는다.
 *   ① 위에는 단계 4개만. 각 단계가 몇 명이고 얼마짜리인지.
 *   ② 하나를 고르면 그 단계만 아래에 펼친다.
 *   ③ 회원 목록 앞에 "이 단계는 뭘 하면 되는지" 가이드를 먼저 놓는다.
 *
 * 회원 목록보다 가이드가 위에 있는 게 핵심이다. 명단을 먼저 보면
 * 이름만 훑고 넘어가지만, 할 일을 먼저 읽으면 명단이 작업 목록이 된다.
 *
 * ─────────────────────────────────────────────────────────────
 * 명단은 요약 줄, 상세는 눌러서 (2026-08 변경)
 * ─────────────────────────────────────────────────────────────
 * 명단이 회원당 260px 짜리 상세 카드라 네 명만 넘어가도 화면 밖으로 밀렸다.
 * 원장님이 여기서 하는 일은 **고르는 것**인데, 비교하려면 스크롤을 위아래로
 * 왕복해야 했다. 그래서 기본은 한 줄짜리 요약(MemberSummaryRow)으로 바꾸고,
 * 상세 카드(MemberRow)는 **그대로** 우측 드로어에 담아 눌렀을 때 연다.
 *
 * ⚠️ 상세 카드는 새로 그리지 않았다. 목록을 열었을 때와 안 열었을 때 화면이
 *    갈리면 원장님이 어느 쪽을 믿어야 할지 알 수 없다.
 * ⚠️ 지시 버튼은 요약 줄에도 남겼다. 상세를 열어야만 지시할 수 있게 하면
 *    급한 회원 셋을 목록에서 찾아 놓고도 세 번 열었다 닫아야 한다.
 */

const BORDER = '#E3EAF3';
/**
 * 칸반에 세우는 단계 — **다섯 칸.**
 *
 * 🔴 여기에 단계를 빠뜨리면 그 회원은 **어느 칸에도 안 그려집니다.**
 *    화면에서도 PDF 에서도 사라지고 「지켜야 할 매출」이 조용히 줄어듭니다.
 *    `MemberStage` 에 값을 더하면 이 배열과 `reportComposer.STAGES` 를
 *    반드시 함께 채워야 합니다 (합계 보존 테스트가 지켜봅니다).
 *
 * 순서는 파이프라인 순입니다 — 첫 수강권에서 시작해 안정으로 가고,
 * 흔들리면 주의를 거쳐 위험으로 내려갑니다.
 */
const STAGES: MemberStage[] = ['FIRST_PASS', 'STABLE', 'WATCH', 'GOLDEN', 'AT_RISK'];

/**
 * 어느 단계부터 보여줄지 — 급한 것이 기본값이어야 한다.
 *
 * 주의는 첫 수강권보다 **뒤**입니다. 첫 수강권은 이탈률이 가장 높은 구간이고
 * (`CHURN_RATE_BY_ORDER[1]`), 주의는 「아직 괜찮다」는 뜻이기 때문입니다.
 */
const PRIORITY: MemberStage[] = ['AT_RISK', 'GOLDEN', 'FIRST_PASS', 'WATCH', 'STABLE'];

/** 토스트 한 줄 — 되돌릴 수 있는 동작이면 그 자리에 되돌리기를 함께 단다 */
type Toast = { text: string; undo?: { label: string; run: () => void } };

/**
 * 지금 열려 있는 「왜 이 금액인가」 팝업 (2026-09-01 QA31).
 *
 * 숫자를 복사해 두지 않고 **무엇을 여는지만** 들고 있습니다. 금액과 목록은
 * 그릴 때 화면이 쓰는 바로 그 변수에서 뽑습니다 — 열어 둔 사이에 목록을 다시
 * 받아 오면 복사본은 낡고, 팝업이 헤더와 다른 값을 말하게 됩니다.
 */
type ValueModalKey =
    | { kind: 'PREPAID_ALL' }
    | { kind: 'EXPECTED_ALL' }
    | { kind: 'PREPAID_STAGE'; stage: MemberStage }
    | { kind: 'EXPECTED_STAGE'; stage: MemberStage }
    /** 카드 배지 — 수강권 한 건의 근거 */
    | { kind: 'CARD'; cardKey: string; axis: 'PREPAID' | 'EXPECTED' };

const OpsBriefingMembersPage = () => {
    const [date, setDate] = useState<Dayjs>(() => dayjs().startOf('day'));
    // 데이터가 오기 전에는 정할 수 없다. 도착하면 가장 급한 단계로 맞춘다.
    const [picked, setPicked] = useState<MemberStage | null>(null);
    const [q, setQ] = useState('');
    const [hoverStage, setHoverStage] = useState<MemberStage | null>(null);
    const [toast, setToast] = useState<Toast | null>(null);
    /** 가린 회원을 다시 꺼내 보고 있나 */
    const [showHidden, setShowHidden] = useState(false);
    /**
     * 상세를 열어 둔 **카드**.
     *
     * ⚠️ `memberId` 가 아니라 `cardKey` 다. 서버는 회원 한 명당 카드 한 장이 아니라
     *    **활성 수강권 한 건당 카드 한 장**을 주므로, memberId 로 들고 있으면
     *    원장이 누른 줄과 다른 줄이 열린다 (adapters.MemberView.cardKey 주석 참고).
     *
     * 식별자만 들고 있는 것은 그대로다 — 카드 객체를 복사해 두면 지시를 보낸 뒤
     * 새로 받아 온 목록과 드로어 안의 값이 어긋난다.
     */
    const [openId, setOpenId] = useState<string | null>(null);
    /** 「왜 이 금액인가」 팝업 — 무엇을 열지만 들고 있다 (ValueModalKey 주석) */
    const [valueModal, setValueModal] = useState<ValueModalKey | null>(null);

    const isToday = date.isSame(dayjs().startOf('day'), 'day');

    const ctx = useOpsContext();
    const { data, isLoading, isError, refetch } = useOpsMembersQuery(
        ctx,
        date.format('YYYY-MM-DD'),
        ctx.ready
    );

    // 서버는 원본만 준다. 순번·단계는 여기서 계산해 붙인다.
    const allMembers = useMemo(() => toMemberViews(data?.members ?? []), [data]);
    const funnel = data?.conversionFunnel;

    /**
     * 서버가 어디까지 아는가 — **키의 존재**로 판정한다.
     *
     * `sameDayAdjustments` 는 `null`(못 쟀다)과 키 없음(구버전 서버)이 다른 뜻이고,
     * `hidden` 은 키가 없으면 이 서버에 가리기 엔드포인트가 아예 없다는 뜻이라
     * 버튼을 그리면 눌러서 404 를 받게 됩니다. 그래서 값이 아니라 키를 봅니다.
     *
     * ⚠️ 낙관 반영은 `hidden` 키를 지우지 않고 true/false 로만 바꾸므로,
     *    가리는 순간 이 판정이 뒤집혀 UI 가 사라지는 일은 없습니다.
     */
    const caps: SummaryCapabilities = useMemo(
        () => ({
            showSameDay: allMembers.some((m) => m.sameDayAdjustments !== undefined),
            showHide: allMembers.some((m) => m.hidden !== undefined),
        }),
        [allMembers]
    );

    /**
     * 가려 둔 회원은 목록·단계 인원·금액 합계에서 **전부** 빠진다.
     *
     * 아래 계산(countOf·valueOf·defendTotal·unclassified …)은 모두 `members` 를
     * 보므로, 여기서 한 번 거르면 화면 전체가 같은 기준을 씁니다.
     * 대신 몇 명을 뺐는지는 합계 옆에 반드시 밝힙니다 — 안 밝히면 원장님이
     * 어제와 다른 합계를 보고 숫자를 못 믿게 됩니다.
     */
    const hiddenMembers = useMemo(() => {
        /*
         * 가리기는 **회원 단위**라 카드가 여러 장이면 전부 hidden 이 된다.
         * 그대로 그리면 같은 회원이 "가린 회원 N명 보기"에 두 번 뜨고 N 도 부풀려진다.
         */
        const seen = new Set<string>();
        return allMembers.filter(
            (m) => m.hidden === true && !seen.has(m.memberId) && (seen.add(m.memberId), true)
        );
    }, [allMembers]);
    const members = useMemo(() => allMembers.filter((m) => m.hidden !== true), [allMembers]);

    const showToast = (next: Toast, ms = 2400) => {
        setToast(next);
        window.setTimeout(() => setToast((cur) => (cur === next ? null : cur)), ms);
    };
    const fire = (msg: string) => showToast({ text: msg });

    /* ── 강사 지시 ────────────────────────────────────────────
     *
     * 예전에는 버튼이 setToast 만 불러 "지시했습니다"를 띄웠습니다.
     * 어디에도 남지 않았고, 원장님은 지시가 나갔다고 믿고 손을 뗐습니다.
     * 오늘의 브리핑과 **같은 엔드포인트**로 실제 지시를 만듭니다 —
     * 만들어진 지시는 오늘의 브리핑 하단 '지시 및 진행 현황'에 나타납니다.
     *
     * 그리고 이제 **같은 확인 창**을 지납니다 (useOrderConfirm).
     * 두 화면이 다르게 동작하면 원장님이 화면마다 다른 흐름을 겪습니다.
     * 실제 전송은 확인 창에서 확정을 눌렀을 때만 일어납니다.
     * ────────────────────────────────────────────────────── */

    const confirm = useOrderConfirm(ctx, {
        notify: (msg) => fire(msg),
        // 이 화면에는 진행 현황이 없다. 어디서 보면 되는지 말해 준다.
        tracker: '오늘의 시그널 > 진행 현황에서 확인하실 수 있습니다',
    });

    /*
     * 선물 발송 — 오늘의 브리핑과 **같은 훅**을 씁니다.
     *
     * 두 화면이 각자 창을 들면 멱등키가 갈려 같은 회원에게 두 번 나갑니다.
     * 키는 화면이 아니라 의도(센터·회원·상품·날짜)에서 나옵니다 — giftSendKey.ts 참고.
     */
    const gift = useGiftSendFlow(ctx, { notify: (msg) => fire(msg) });

    /**
     * 선물 확인 창을 연다.
     *
     * 화면이 보여준 진단 문장을 그대로 발송 사유로 싣는다 (handleOrder 와 같은 규칙).
     * 실제 발송은 확인 창에서 한 번 더 확정해야 일어난다.
     */
    const handleGift = (m: MemberView, task: string) => {
        gift.open({
            memberId: m.memberId,
            memberName: m.name,
            summary: task,
            dateKey: date.format('YYYY-MM-DD'),
        });
    };

    /** 며칠 전 데이터를 보고 있나 — 지시 문구에 남긴다 */
    const daysAgo = dayjs().startOf('day').diff(date, 'day');

    /* ── 회원 가리기 ──────────────────────────────────────────
     *
     * ⚠️ 오늘의 브리핑의 **항목 가리기와 다른 물건**입니다.
     *    항목 가리기는 (날짜, itemId) 로 그날 카드 하나를 내리는 것이고,
     *    이것은 회원 한 명을 **다시 보기 전까지 영구히** 내립니다.
     *    그래서 요청에 날짜가 없습니다(useSetOpsMemberHiddenMutation).
     *
     * 🔴 **백엔드 v9 부터 브리핑 생성에서도 제외됩니다** (2026-08-31).
     *    그 전에는 이 표에서만 내려가고 다음 날 아침 브리핑에는 그대로
     *    올라왔습니다. 문구가 「회원 현황에서 숨겨집니다」였던 이유가 그것이고,
     *    이제는 두 곳 모두에서 빠지므로 문구도 그렇게 말해야 합니다.
     *
     * 낙관 반영과 실패 롤백은 훅이 맡습니다. 여기서는 **성공했을 때만**
     * 성공 문구를 띄웁니다 — 서버에 아직 엔드포인트가 없으면 404 가 오고,
     * "가렸습니다" 라고 해 놓고 새로고침에 되돌아오는 것이 가장 나쁩니다.
     * ────────────────────────────────────────────────────── */

    const setHidden = useSetOpsMemberHiddenMutation(ctx, date.format('YYYY-MM-DD'));
    /** 지금 가리기·다시 보기 요청이 나가 있는 회원 (그 줄만 잠근다) */
    const [pendingHideId, setPendingHideId] = useState<string | null>(null);

    const failMessage = (error: unknown) => {
        const msg = (error as { response?: { data?: { message?: string } } } | null)?.response?.data
            ?.message;
        return msg || '처리하지 못했습니다. 잠시 후 다시 시도해 주세요';
    };

    /** 가린 회원을 다시 목록으로 (서버에서도 해제된다) */
    const handleUnhide = (m: MemberView) => {
        if (setHidden.isLoading) return;
        setPendingHideId(m.memberId);
        setHidden.mutate(
            { memberId: m.memberId, hidden: false },
            {
                onSuccess: () => fire(`${m.name} 회원을 다시 표시합니다 · 오늘의 시그널에도 다시 오릅니다`),
                onError: (e) => fire(failMessage(e)),
                onSettled: () => setPendingHideId(null),
            }
        );
    };

    const handleHide = (m: MemberView) => {
        if (setHidden.isLoading) return;
        /* 상세를 열어 둔 채 가리면 목록에 없는 회원의 상세만 남는다 */
        /* 가리기는 회원 단위라 그 회원의 카드가 열려 있으면 전부 닫는다 */
        if (opened?.memberId === m.memberId) setOpenId(null);
        setPendingHideId(m.memberId);
        setHidden.mutate(
            { memberId: m.memberId, hidden: true },
            {
                onSuccess: () =>
                    showToast(
                        {
                            text: `${m.name} 회원을 가렸습니다 · 다시 보기 전까지 회원 현황과 오늘의 시그널에서 모두 제외됩니다`,
                            undo: { label: '되돌리기', run: () => handleUnhide(m) },
                        },
                        /* 되돌리기를 누를 시간을 준다 — 오늘의 브리핑 가리기와 같은 6초 */
                        6000
                    ),
                onError: (e) => fire(failMessage(e)),
                onSettled: () => setPendingHideId(null),
            }
        );
    };

    /**
     * 지시 확인 창 열기.
     *
     * task 문구 규칙은 오늘의 브리핑과 같습니다 —
     * **화면이 원장님께 보여준 판단 문장을 그대로 싣습니다.**
     * (OpsBriefingTodayPage.handleAction 은 item.title 을 씁니다)
     * 원장이 읽고 누른 문장과 강사가 받을 문장이 다르면, 나중에
     * "나는 그런 지시 한 적 없는데"가 됩니다.
     */
    const handleOrder = (m: MemberView, task: string) => {
        /*
         * 담당 강사가 없으면 보낼 곳이 없다.
         * 서버도 (memberId, coachId) 담당 관계를 확인하고 400 을 준다 —
         * 화면에서 먼저 막아 실패 토스트를 보게 하지 않는다.
         * (확인 창도 coachId 가 없으면 열리지 않는다)
         */
        if (!m.coachId) return;

        confirm.ask({
            memberId: m.memberId,
            memberName: m.name,
            coachId: m.coachId,
            coachName: m.coachName,
            task,
            /*
             * memberDraft 를 넘기지 않는다.
             *
             * 이 화면에는 회원용 문구 초안이 없고, 확인 창도 이제 그 칸을 두지 않는다
             * (대표 결정 — 지시는 강사에게 가고, 회원에게 할 말은 강사가 정한다).
             * 안 넘기면 요청 본문에서 키가 통째로 빠지고, 저장값은 null 이 되어
             * 강사 앱이 회원 이름과 근거로 첫 마디를 만든다 (기존 동작).
             */
            /*
             * 강사에게 덧붙일 말은 비워서 연다.
             *
             * 예전에는 여기에 `N일 전 회원 현황을 보고 남긴 지시입니다.` 를 박아 넣었다.
             * 뺀 이유는 세 가지다.
             *
             *  1. 강사가 할 일과 무관하다. ownerNote 는 강사 앱에서 「원장님 메모」라는
             *     인용 상자로 뜬다 — 원장이 직접 한 말이 있을 때만 떠야 하는 자리다.
             *     원장이 언제 어느 화면을 봤는지는 강사가 알아서 할 수 있는 일이 없다.
             *     매번 이 문장이 박혀 나가면, 정작 원장이 진짜로 남긴 말도 같은 회색 상자라
             *     강사가 구분해서 읽지 못한다.
             *  2. 원장에게는 이미 보인다. 같은 사실을 contextNote 로 확인 창과 완료 토스트에
             *     적고 있다 (아래 contextNote). 원장이 볼 말을 강사 칸에 넣을 이유가 없다.
             *  3. 기록은 안 잃는다. "며칠 전 회원 현황을 보고 낸 지시"라는 사실은 sourceDate 로
             *     구조화해서 이미 보내고 서버가 저장한다(opsOrder.sourceDate). 사람이 읽는
             *     문장에 실어 보내는 것보다 정확하다.
             */
            ownerNote: '',
            /*
             * 금액을 모르는 수강권(무료권·서비스 세션)은 null 이다. 0 이 아니다.
             * 0 으로 보내면 '지켜낸 매출' 합계에 0원짜리 지시로 섞여 합계가 낮아진다.
             *
             * 🔴 오늘의 브리핑의 `orderValueOf` 와 **같은 규칙**이다 —
             *    걸린 돈 = 미사용 선수금 + 재등록 기대, 한 수강권 안에서만 더한다.
             *    두 화면이 다른 금액을 실어 보내면 지시 트래커 합계가 화면마다 달라진다.
             */
            valueAmount:
                m.value == null || (m.value.prepaid == null && m.value.expected == null)
                    ? null
                    : (m.value.prepaid ?? 0) + (m.value.expected ?? 0),
            /*
             * 🔴 `valueKind` 는 두 값 중 **큰 쪽의 성격**으로 정한다 (교차검증 지적).
             *    「기대가 조금이라도 있으면 GAIN」으로 두면, 선수금 80만 + 기대 5만인
             *    지시가 트래커에서 **85만원 전체가 「확보 가능」**으로 읽힌다.
             *    두 축을 한 필드에 저장하는 계약이라 손실은 피할 수 없고, 그렇다면
             *    금액의 대부분이 어느 쪽인지를 말하는 것이 덜 틀린다.
             */
            valueKind: (m.value?.expected ?? 0) > (m.value?.prepaid ?? 0) ? 'GAIN' : 'DEFEND',
            /*
             * sourceItemId 는 보내지 않는다 — 이 화면은 브리핑 항목이 아니다.
             * 서버는 sourceItemId 와 sourceDate 가 **둘 다** 있을 때만
             * 브리핑 항목을 ACTED 로 바꾸므로(opsBriefing.service.createOrder),
             * 날짜만 보내면 엉뚱한 항목이 처리됨으로 바뀌는 일은 없다.
             */
            sourceDate: date.format('YYYY-MM-DD'),
            contextNote: daysAgo > 0 ? `${daysAgo}일 전 회원 현황을 보고 계십니다` : undefined,
            /*
             * reason·facts·sourceDraft 를 넘기지 않는다 (미리보기용 값들).
             *
             * 이 화면은 브리핑 항목이 아니라 소스가 없고, 서버도 근거를 지어내지
             * 않는다 (opsBriefing.service.loadSourceSnapshot: 소스가 없으면 전부 빈 값).
             * 그러니 확인 창의 강사 화면 미리보기도 근거 없는 카드로 보여야 맞다 —
             * 여기서 이 화면의 수치를 채워 넣으면, 원장님은 강사가 근거를 함께
             * 받는 줄 알지만 실제 강사 카드에는 아무것도 없다.
             */
        });
    };

    /*
     * ─────────────────────────────────────────────────────────────
     * 「N명」은 **회원 수**여야 한다 — 이 목록의 줄 수가 아니다
     * ─────────────────────────────────────────────────────────────
     * 서버는 활성 수강권 한 건당 카드 한 장을 준다. 그래서 수강권을 둘 가진 회원은
     * 줄이 두 개이고, 예전 코드는 그 줄 수를 그대로 「회원 N명」이라고 불렀다.
     * 원장님이 회원 명부와 대조하면 인원이 맞지 않는다.
     *
     * 금액 합계(valueOf)는 반대로 **카드 단위 합이 맞다.** 수강권마다 걸린 돈이
     * 따로 있기 때문이다. 인원만 회원 단위로 센다.
     */
    const distinctMembers = (list: MemberView[]) => new Set(list.map((m) => m.memberId)).size;

    /** 순번을 확정할 수 없는 회원 — 판단 근거가 비어 있으니 먼저 알려야 한다 */
    const unclassified = members.filter((m) => needsClassification(m.passOrder));
    const unclassifiedCount = distinctMembers(unclassified);

    const countOf = (st: MemberStage) => distinctMembers(members.filter((m) => m.stage === st));
    /**
     * 단계 카드의 **두 숫자** — 정책 M-D3.
     *
     * 예전에는 한 숫자였고, 그 숫자의 뜻이 단계마다 달랐습니다 —
     * ①이탈위험·④첫수강권·주의는 「잔여 + 기대」, ②안정·③골든타임은 「기대만」.
     * 네 칸을 나란히 놓고 비교할 수 없는 숫자들이었습니다.
     *
     * 이제 두 칸이 늘 같은 뜻입니다.
     *   · 미사용 선수금 — 수강권마다 따로 받은 돈이라 **카드별로 더한다**
     *   · 재등록 기대   — 회원이 다음에 한 번 더 산다는 기댓값이라 **회원당 한 번만**
     *     센다 (`pickPrimaryCards`). 수강권 셋인 회원의 다음 결제를 세 번 세면
     *     「확보 가능한 매출」이 그대로 세 배가 된다.
     *
     * 단가가 없는 수강권(무료권·체험)은 value 가 null 이다. 0으로 더하면
     * "가치 없는 회원"으로 세는 셈이라 아예 뺀다 — 대신 몇 명이 빠졌는지 밝힌다.
     */
    const prepaidOf = (st: MemberStage) => sumPrepaid(members.filter((m) => m.stage === st));
    /*
     * 🔴 **대표 카드를 먼저 고르고 나서 단계로 나눕니다.** 순서를 뒤집으면 안 됩니다.
     *
     * 단계마다 따로 대표를 뽑으면(`pickPrimaryCards(단계 안 목록)`), 수강권이 두
     * 단계에 걸친 회원은 **양쪽에서 각각 대표가 되어** 기대가 두 번 잡힙니다.
     * 실서버 279건에서 그 차이가 2,374만 원이었습니다 — 네 칸을 더한 값이 헤더보다
     * 크고, 원장님이 검산하면 바로 어긋납니다.
     *
     * 지금 순서로는 회원의 기대가 **대표 수강권이 있는 단계 한 곳**에만 들어가서
     * 네 칸의 합이 헤더의 「확보 가능한 매출」과 정확히 같습니다.
     */
    const primaryCards = useMemo(() => pickPrimaryCards(members), [members]);
    const expectedOf = (st: MemberStage) =>
        sumExpected(primaryCards.filter((m) => m.stage === st));
    /**
     * 이 단계에서 금액을 못 구한 **회원** 수.
     *
     * ⚠️ 판정 범위는 **이 단계 안**이다. "다른 단계 카드에 금액이 있으니 이 회원은
     *    산정 불가가 아니다" 로 넓히면 안 된다 — 골든타임 줄이 「산정 불가」인데
     *    안정 궤도 카드에 금액이 있다는 이유로 골든타임의 안내가 0명이 되어,
     *    화면에 보이는 줄과 그 위의 설명이 어긋난다.
     *
     * 반대로 이 단계 안에서는 회원 단위로 센다 — 같은 단계에 그 회원의 카드가
     * 둘인데 하나만 산정 불가면, 그 회원 몫은 이미 합계에 들어가 있으므로
     * "산정 불가 회원"으로 세면 두 번 세는 셈이다.
     */
    const unpricedOf = (st: MemberStage) => {
        const inStage = members.filter((m) => m.stage === st);
        const priced = new Set(inStage.filter((m) => m.value != null).map((m) => m.memberId));
        return distinctMembers(inStage.filter((m) => !priced.has(m.memberId)));
    };

    /**
     * 실제로 보여줄 단계.
     * 아직 고르지 않았으면 회원이 있는 단계 중 가장 급한 곳을 연다.
     * 데이터가 없으면 골든타임을 기본으로 둔다 (빈 화면이라 어차피 안 보인다).
     */
    const stage: MemberStage =
        picked ?? PRIORITY.find((st) => members.some((m) => m.stage === st)) ?? 'GOLDEN';

    /**
     * 급한 순서로 정렬 — 목록 맨 위가 곧 오늘 처리할 사람.
     *
     * 규칙은 `memberSort.sortMembersForStage` 한 곳에 있다. 여기서 다시 적으면
     * 「첫 수강권을 앞으로 당기되 급한 순서는 뒤집지 않는다」가 눈으로만 확인된다.
     */
    const list = useMemo(() => {
        const t = q.trim();
        const picked_ = members
            .filter((m) => m.stage === stage)
            .filter((m) => !t || m.name.includes(t) || m.coachName.includes(t));
        return sortMembersForStage(picked_, stage);
    }, [members, stage, q]);

    /**
     * 상세를 열어 둔 카드.
     *
     * 목록(list)이 아니라 전체(members)에서 찾는다 — 검색어를 고치거나 단계를
     * 바꾸는 순간 보던 상세가 사라지면 "방금 뭘 보고 있었지"가 된다.
     * 단계도 그 카드 자신의 값(m.stage)을 쓴다. 화면에서 고른 단계를 쓰면
     * 탭을 옮겼을 때 상세가 남의 단계 기준으로 판정된다.
     *
     * ⚠️ `cardKey` 로 찾는다. 못 찾으면 **그냥 닫힌다** — 다시 받아 온 목록에
     *    그 수강권이 없어졌다는 뜻이고, 비슷한 카드를 대신 여는 것보다 낫다.
     *    (그게 지금 고치는 버그다: 누른 줄과 다른 줄이 열리던 것)
     */
    const opened = openId ? (members.find((m) => m.cardKey === openId) ?? null) : null;
    /**
     * 이 회원의 **다른 수강권 카드**.
     *
     * 한 회원이 활성 수강권을 둘 이상 가지면 목록에 줄이 여러 개 뜬다.
     * 상세만 보고 있으면 "이 회원에게 걸린 게 이게 전부"라고 읽으므로,
     * 나머지 수강권을 내역으로 함께 보여 준다.
     *
     * ⚠️ **합계를 만들지 않는다.** 카드 금액에는 각각 "다음 수강권 예상 결제액 ×
     *    재등록률"이 들어 있어 두 장을 더하면 다음 재등록 기대액이 두 번 더해진다.
     *    DEFEND(지켜야 할 돈)와 GAIN(확보 가능한 돈)도 성격이 달라 한 숫자로
     *    합치면 화면이 일부러 갈라 놓은 두 축이 다시 섞인다.
     *    그래서 **수강권별로 따로** 적는다.
     */
    const openedSiblings = opened
        ? members.filter((m) => m.memberId === opened.memberId && m.cardKey !== opened.cardKey)
        : [];

    const meta = STAGE_META[stage];
    const guide = STAGE_GUIDE[stage];
    /**
     * 전환율.
     * 분자를 모르면(converted null) 비율 자체가 없습니다.
     * 0% 로 적으면 "한 명도 전환 못 시켰다"가 되어 정반대로 읽힙니다.
     */
    const convPct =
        funnel && funnel.firstPassEnded && funnel.converted != null
            ? Math.round((funnel.converted / funnel.firstPassEnded) * 100)
            : null;

    /**
     * 🔴 퍼널 **집계가 실패**했는가 — 「이 달 대상이 없다」와 다른 사실이다.
     *
     * 서버는 이 집계가 깨져도 500 을 내지 않고 `firstPassEnded: null` 로 내린다
     * (회원 현황 전체가 안 열리는 것보다 낫다). 그런데 화면이 그것을
     * 「전환율 집계 전」이라고만 적으면 **장애와 미집계가 구분되지 않아서**
     * 원장은 기다리면 채워질 값으로 읽는다. 실제로는 다시 불러와야 한다.
     */
    const funnelBroken = !!funnel && funnel.firstPassEnded == null;

    /*
     * 헤더의 두 숫자 — 정책 M2 · M4 (2026-08-31 대표 확정본).
     *
     * 💰 지켜야 할 매출  = Σ 미사용 선수금, **전 회원**
     * 📈 확보 가능한 매출 = Σ 재등록 기대, **전 회원 · 회원당 1회**
     *
     * 🔴 예전에는 회원 **단계**로 두 통을 갈랐습니다(`valueKindOf`).
     *    그래서 골든타임·안정 궤도 회원 — 센터에서 선수금을 가장 많이 들고 있는
     *    집단 — 의 잔여분이 **어느 합계에도 안 잡혔습니다.** 이제 선수금은
     *    단계와 무관하게 전부 왼쪽에, 기대는 전부 오른쪽에 들어갑니다.
     */
    const defendTotal = sumPrepaid(members);
    const gainTotal = sumExpected(primaryCards);
    /*
     * 여러 단계에 걸쳐 선 회원 수 — 단계 인원을 더하면 전체 회원 수보다 많아진다.
     *
     * 카드가 수강권 단위라 한 회원이 「안정 궤도」와 「골든타임」에 동시에 설 수 있다.
     * 각 단계의 「N명」은 그 단계 안에서 중복 없이 센 값이지만, 넷을 더하면 겹친
     * 회원이 두 번 세어진다. **안 밝히면 원장님이 더해 보고 숫자를 못 믿는다** —
     * 실서버에서 205명이 단계 합으로는 224명이 된다.
     */
    const multiStageMembers = (() => {
        const stages = new Map<string, Set<MemberStage>>();
        members.forEach((m) => {
            const s = stages.get(m.memberId) ?? new Set<MemberStage>();
            s.add(m.stage);
            stages.set(m.memberId, s);
        });
        return Array.from(stages.values()).filter((s) => s.size > 1).length;
    })();
    /** 금액을 계산하지 못한 회원 수 — 합계 밑에 밝혀야 숫자를 믿을 수 있다 */
    /*
     * 금액을 한 푼도 못 구한 **회원** 수.
     * 수강권 하나라도 금액이 잡혔으면 그 회원 몫은 이미 위 합계에 들어가 있으므로
     * 「금액 합계에서 제외」된 사람이 아니다.
     */
    const unpricedTotal = (() => {
        const priced = new Set(members.filter((m) => m.value != null).map((m) => m.memberId));
        return distinctMembers(members.filter((m) => !priced.has(m.memberId)));
    })();

    /**
     * 「왜 이 금액인가」 팝업 (2026-09-01 QA31).
     *
     * 🔴 **합계는 화면이 이미 그린 그 변수를 그대로 넘깁니다.** 여기서 다시 더하면
     *    팝업이 헤더와 다른 값을 말할 수 있고, 근거를 보여주려고 연 창이 오히려
     *    숫자를 못 믿게 만듭니다. 목록도 그 합계를 만든 **바로 그 목록**입니다 —
     *    선수금은 카드 전부, 기대는 대표 수강권만(`primaryCards`). 분모가 다릅니다.
     */
    const valueModalNode = (() => {
        if (!valueModal) return null;
        const close = () => setValueModal(null);

        /* 카드 배지 — 수강권 한 건의 근거. 합계도 목록도 그 한 건이다 */
        if (valueModal.kind === 'CARD') {
            /*
             * 목록에서 다시 찾는다. 못 찾으면 그냥 안 연다 — 다시 받아 온 목록에
             * 그 수강권이 없어졌다는 뜻이고, 낡은 복사본을 보여주는 것보다 낫다
             * (상세 드로어가 `cardKey` 를 다루는 방식과 같다).
             */
            const card = members.find((x) => x.cardKey === valueModal.cardKey);
            if (!card) return null;
            const isPrepaid = valueModal.axis === 'PREPAID';
            return (
                <ValueBreakdownModal
                    single
                    title={isPrepaid ? '미사용 선수금' : '재등록 기대'}
                    definition={isPrepaid ? PREPAID_DEFINITION : EXPECTED_DEFINITION}
                    scopeNote={`${card.name} · ${card.product} 한 건 기준`}
                    rows={isPrepaid ? memberPrepaidRows([card]) : memberExpectedRows([card])}
                    total={(isPrepaid ? card.value?.prepaid : card.value?.expected) ?? 0}
                    policyNotes={
                        isPrepaid ? PREPAID_NOTES : expectedNotes('이탈 위험·주의 단계')
                    }
                    onClose={close}
                />
            );
        }

        const scope = valueModal.kind.endsWith('_STAGE')
            ? (valueModal as { stage: MemberStage }).stage
            : null;
        const scopeLabel = scope ? `${STAGE_META[scope].label} 단계` : '전 회원';
        const cardsInScope = scope ? members.filter((x) => x.stage === scope) : members;
        const primaryInScope = scope
            ? primaryCards.filter((x) => x.stage === scope)
            : primaryCards;

        if (valueModal.kind === 'PREPAID_ALL' || valueModal.kind === 'PREPAID_STAGE') {
            return (
                <ValueBreakdownModal
                    title="지켜야 할 매출"
                    definition={PREPAID_DEFINITION}
                    scopeNote={`${scopeLabel} · 활성 수강권 ${cardsInScope.length}건 기준`}
                    rows={memberPrepaidRows(cardsInScope)}
                    /* 헤더·카드가 그린 그 숫자 그대로 */
                    total={scope ? prepaidOf(scope) : defendTotal}
                    unavailable={{
                        count: countUnpricedCards(cardsInScope),
                        reason: UNPRICED_REASON,
                    }}
                    policyNotes={PREPAID_NOTES}
                    onClose={close}
                />
            );
        }

        return (
            <ValueBreakdownModal
                title="확보 가능한 매출"
                definition={EXPECTED_DEFINITION}
                /* 분모가 다르다 — 회원당 대표 수강권 한 건만 센다 (정책 M7) */
                scopeNote={`${scopeLabel} · 회원 ${primaryInScope.length}명 기준 (회원당 1건)`}
                rows={memberExpectedRows(primaryInScope)}
                total={scope ? expectedOf(scope) : gainTotal}
                unavailable={{
                    count: countUnpricedCards(primaryInScope),
                    reason: UNPRICED_REASON,
                }}
                policyNotes={expectedNotes('이탈 위험·주의 단계')}
                onClose={close}
            />
        );
    })();

    /* ── 상태 분기 ─────────────────────────────────────────── */

    if (!ctx.ready) {
        return (
            <OpsError
                title="센터 정보를 확인할 수 없습니다"
                detail="로그인 정보가 만료되었을 수 있습니다"
            />
        );
    }

    if (isLoading) {
        return (
            <div className="flex w-full flex-col pb-[40px]" style={{ gap: 14 }}>
                <DateNavigator value={date} onChange={setDate} label="회원 현황" />
                <OpsLoading variant="page" />
            </div>
        );
    }

    if (isError) {
        return (
            <div className="flex w-full flex-col pb-[40px]" style={{ gap: 14 }}>
                <DateNavigator value={date} onChange={setDate} label="회원 현황" />
                <OpsError onRetry={() => refetch()} />
            </div>
        );
    }

    /*
     * ⚠️ `members`(가린 회원 뺀 목록)가 아니라 **`allMembers`** 를 봅니다.
     * 회원을 전부 가려 두면 members 는 0 이 되는데, 그때 이 빈 화면으로 빠지면
     * "가린 회원 N명 보기"가 화면에서 통째로 사라져 되돌릴 길이 없어집니다.
     * (아래 목록 영역이 그 경우를 따로 말합니다)
     */
    if (allMembers.length === 0) {
        return (
            <div className="flex w-full flex-col pb-[40px]" style={{ gap: 14 }}>
                <DateNavigator value={date} onChange={setDate} label="회원 현황" />
                {!isToday && (
                    <PastDateBanner date={date} onBackToToday={() => setDate(dayjs().startOf('day'))} />
                )}
                <OpsEmpty
                    icon="👥"
                    title="아직 관리할 회원이 없습니다"
                    body={
                        isToday
                            ? '수강권을 등록한 회원이 생기면 여기에 단계별로 나뉘어 표시됩니다.'
                            : `${date.format('M월 D일')}에는 관리 대상 회원이 없었습니다.`
                    }
                    hint="회원 등록 후 수업 기록이 2주쯤 쌓이면 소진 페이스와 이탈 위험을 판단할 수 있습니다."
                />
            </div>
        );
    }

    return (
        <div className="flex w-full flex-col pb-[40px]" style={{ gap: 14 }}>
            <DateNavigator value={date} onChange={setDate} label="회원 현황" />
            {isOpsBriefingMock() && <OpsMockBadge />}
            {!isToday && <PastDateBanner date={date} onBackToToday={() => setDate(dayjs().startOf('day'))} />}

            {/*
              분류가 안 된 수강권 알림.
              순번은 이탈 위험 판정의 뿌리라, 분류가 비어 있으면 판단 자체를 믿을 수 없다.
              조용히 넘기지 않고 몇 명인지 먼저 알린다.
            */}
            {unclassifiedCount > 0 && (
                <div
                    className="flex w-full items-center rounded-[12px]"
                    style={{ background: '#FEF3C7', border: '1px solid #F0D9A8', padding: '13px 18px', gap: 10 }}
                >
                    <span style={{ fontSize: 16 }}>⚠️</span>
                    <span style={{ fontSize: 14.5, color: '#8A5A00', fontWeight: 700, flex: 'none' }}>
                        수강권 분류가 안 된 회원 {unclassifiedCount}명
                    </span>
                    <span style={{ fontSize: 13.5, color: '#A07B2E' }}>
                        {Array.from(new Set(unclassified.map((u) => u.name))).join(', ')} — 신규인지 재등록인지 정해지지 않아 수강권
                        순번을 확정할 수 없습니다. 순번이 바뀌면 이탈 위험 판정도 달라집니다.
                    </span>
                </div>
            )}

            {/* 파이프라인 요약 */}
            <div
                className="w-full overflow-hidden rounded-[16px]"
                style={{
                    background: 'linear-gradient(135deg, #1A305D 0%, #24406F 55%, #2B5183 100%)',
                    boxShadow: '0 10px 30px rgba(26,48,93,0.18)',
                }}
            >
                <div className="flex items-center justify-between" style={{ padding: '22px 30px' }}>
                    <div className="flex flex-col">
                        <span style={{ fontSize: 13, color: '#9DB4D8' }}>
                            회원 파이프라인{funnel ? ` · ${funnel.month}` : ''}
                        </span>
                        <span
                            style={{
                                fontSize: 26,
                                fontWeight: 800,
                                color: '#FFFFFF',
                                marginTop: 6,
                                letterSpacing: '-0.03em',
                            }}
                        >
                            관리 중인 회원 {distinctMembers(members)}명
                        </span>
                        {unpricedTotal > 0 && (
                            /*
                              합계에 안 들어간 사람이 있으면 밝혀야 숫자를 믿을 수 있다.
                              이 안내는 여러 회원을 한꺼번에 가리키므로 두 가지 이유를 다 적는다 —
                              단가가 없는 수강권과, 회차 개념이 없는 수강권(무제한·대관)이다.
                            */
                            <span
                                title={`${VALUE_UNAVAILABLE_REASON}\n\n회차 개념이 없는 수강권(무제한·대관)도 여기에 포함됩니다. 등록상 999회를 회차로 세면 없는 매출이 합계에 들어갑니다.`}
                                style={{
                                    fontSize: 12,
                                    color: '#9DB4D8',
                                    marginTop: 7,
                                    cursor: 'help',
                                }}
                            >
                                ※ 금액을 알 수 없는 회원 {unpricedTotal}명은 합계에서 제외
                            </span>
                        )}
                        {/*
                          가린 회원도 합계에서 빠진다. 밝히지 않으면 원장님은
                          어제와 다른 인원·금액을 보고 숫자를 못 믿게 된다.
                          (오늘의 브리핑의 "가린 N건 제외" 와 같은 문법)
                        */}
                        {hiddenMembers.length > 0 && (
                            <span
                                title="가린 회원은 목록·단계 인원·금액 합계에서 모두 빠지고, 오늘의 시그널 생성에서도 제외됩니다. 아래 「가린 회원 N명 보기」에서 되돌릴 수 있습니다."
                                style={{
                                    fontSize: 12,
                                    color: '#9DB4D8',
                                    marginTop: 5,
                                    cursor: 'help',
                                }}
                            >
                                ※ 가린 회원 {hiddenMembers.length}명 제외
                            </span>
                        )}
                    </div>
                    <div className="flex items-center">
                        {[
                            {
                                label: '💰 지켜야 할 매출',
                                v: defendTotal,
                                tone: '#FFC9CD',
                                hint: PREPAID_DEFINITION,
                                open: () => setValueModal({ kind: 'PREPAID_ALL' }),
                            },
                            {
                                label: '📈 확보 가능한 매출',
                                v: gainTotal,
                                tone: '#8FE3CD',
                                hint: EXPECTED_DEFINITION,
                                open: () => setValueModal({ kind: 'EXPECTED_ALL' }),
                            },
                        ].map((x, i) => (
                            <div
                                key={x.label}
                                className="flex flex-col"
                                style={{
                                    padding: '0 26px',
                                    borderLeft: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.14)',
                                }}
                            >
                                <span
                                    title={x.hint}
                                    style={{ fontSize: 12.5, color: '#9DB4D8', cursor: 'help' }}
                                >
                                    {x.label}
                                </span>
                                {/*
                                  🔴 금액을 누르면 **누가 얼마씩 들어갔는지**가 펴진다 (QA31).
                                     툴팁은 정의만 말할 수 있고 내역은 못 말한다. 답 못 하는
                                     금액은 결국 안 믿게 된다.
                                     시각은 절제한다 — 점선 밑줄과 커서뿐, 자리는 그대로.
                                */}
                                <div className="flex items-baseline" style={{ gap: 3, marginTop: 4 }}>
                                    <ValueTrigger
                                        label={`${x.label} 산출 근거 보기`}
                                        onClick={x.open}
                                        style={{
                                            fontSize: 25,
                                            fontWeight: 800,
                                            color: x.tone,
                                            letterSpacing: '-0.03em',
                                        }}
                                    >
                                        {won(x.v)}
                                    </ValueTrigger>
                                    <span style={{ fontSize: 13, color: '#9DB4D8' }}>원</span>
                                </div>
                            </div>
                        ))}
                        {/*
                          🔴 재등록 전환율을 헤더로 올렸다 (2026-08-31 QA17, 대표 제안).
                             「종료 25명 중 10명 전환 = 40%」는 이 화면에서 가장 값진
                             숫자인데 맨 아래 띠에 묻혀 있었다. 원장이 제일 먼저 보는
                             자리로 올린다. 아래 상세 띠는 그대로 둔다(경로 두 개가 아니라
                             요약 + 상세다).

                          ⚠️ **톤 기준선은 아래 띠와 같은 상수여야 한다.**
                             여기에 52.9 를 숫자로 박으면, 실측이 갱신될 때 위아래가
                             서로 다른 기준으로 색을 칠한다. 694행 주석의 사고가
                             정확히 그것이었다(기준선만 32 로 박혀 있었다).

                          ⚠️ **평균 미달을 빨강으로 칠하지 않는다.** 이건 참고 지표지
                             오늘 처리할 일이 아니다. 헤더의 빨강은 「지켜야 할 매출」이
                             이미 쓰고 있어서, 여기까지 빨강이면 아침에 열자마자
                             화면이 경고 두 개로 시작한다. 높으면 초록, 낮으면 흰색.
                        */}
                        {convPct != null && (
                            <div
                                className="flex flex-col"
                                style={{
                                    padding: '0 26px',
                                    borderLeft: '1px solid rgba(255,255,255,0.14)',
                                }}
                            >
                                <span style={{ fontSize: 12.5, color: '#9DB4D8', whiteSpace: 'nowrap' }}>
                                    🔁 재등록 전환율
                                </span>
                                <div className="flex items-baseline" style={{ gap: 6, marginTop: 4 }}>
                                    <span
                                        style={{
                                            fontSize: 25,
                                            fontWeight: 800,
                                            letterSpacing: '-0.03em',
                                            whiteSpace: 'nowrap',
                                            color:
                                                convPct >= FIRST_PASS_REREGISTRATION_RATE
                                                    ? '#8FE3CD'
                                                    : '#FFFFFF',
                                        }}
                                    >
                                        {convPct}%
                                    </span>
                                    <span style={{ fontSize: 13, color: '#9DB4D8', whiteSpace: 'nowrap' }}>
                                        평균 {FIRST_PASS_REREGISTRATION_RATE}%
                                    </span>
                                </div>
                                {/*
                                  분모를 같이 적는다. 종료가 3명일 때의 33% 와
                                  80명일 때의 33% 는 같은 숫자가 아닌데, 비율만
                                  크게 띄우면 둘이 똑같아 보인다.
                                */}
                                <span
                                    title={
                                        /*
                                         * 🔴 서버는 **그 달 코호트의 상태 행 수**를 센다 —
                                         *    회원 중복제거도, 수강권 순번 판정도 하지 않는다.
                                         *    「첫 수강권이 끝난 N명」이라고 적으면 세지도 않은
                                         *    것을 셌다고 말하는 것이다. 세는 그대로 적는다.
                                         */
                                        `이번 달 종료한 수강권 ${funnel?.firstPassEnded}건 중 ` +
                                        `${funnel?.converted}건이 재등록으로 이어졌습니다. ` +
                                        '수강권 건수라 한 회원이 여러 건일 수 있습니다.\n\n' +
                                        `⚠️ 「라포 평균 ${FIRST_PASS_REREGISTRATION_RATE}%」는 전체 센터의 ` +
                                        '**1번째 수강권** 코호트 평균이라, 위 숫자와 **모집단이 다릅니다.** ' +
                                        '같은 것을 잰 두 값이 아니니 크기를 직접 견주지 마시고 추세로만 보세요. ' +
                                        '우리 센터 한 달치는 표본도 작아 몇 건 차이로 크게 흔들립니다.'
                                    }
                                    style={{
                                        fontSize: 11.5,
                                        color: '#8FA5C4',
                                        marginTop: 3,
                                        whiteSpace: 'nowrap',
                                        cursor: 'help',
                                    }}
                                >
                                    종료 {funnel?.firstPassEnded}건 중 {funnel?.converted}건 재등록
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div
                    className="flex items-center"
                    style={{
                        padding: '12px 30px',
                        borderTop: '1px solid rgba(255,255,255,0.12)',
                        background: 'rgba(0,0,0,0.12)',
                        gap: 14,
                    }}
                >
                    <span style={{ fontSize: 13, color: '#B7C7E0', flex: 'none' }}>
                        종료 수강권 → 재등록
                    </span>
                    {/*
                      🔴 단위는 **건**이고 마지막 칸은 **재등록**이다.
                         서버가 세는 것은 그 달 코호트의 **상태 행 수**라 회원 중복제거도
                         순번 판정도 없다(위 요약 문구와 같은 값이다). 띠만 「명」으로 두면
                         한 화면에서 위는 「25건」, 아래는 「25명」이 되어 원장은
                         **두 숫자를 다른 것으로** 읽는다.
                    */}
                    {[
                        { l: '종료', v: funnel?.firstPassEnded ?? null },
                        { l: '상담', v: funnel?.consulted ?? null },
                        { l: '재등록', v: funnel?.converted ?? null },
                    ].map((x, i) => (
                        <span key={x.l} className="flex items-baseline" style={{ gap: 5 }}>
                            {i > 0 && <span style={{ color: '#5C7BA5', marginRight: 8 }}>›</span>}
                            <span style={{ fontSize: 12.5, color: '#8FA5C4' }}>{x.l}</span>
                            {/* 세지 않은 값을 0건으로 적으면 "아무것도 안 했다"가 된다 */}
                            <span
                                style={{ fontSize: 17, fontWeight: 800, color: '#FFFFFF' }}
                                title={
                                    /*
                                     * 🔴 **집계 실패와 미집계를 같은 말로 적지 않는다.**
                                     *    위 캡션은 세 갈래로 갈라 놨는데 이 숫자들의 툴팁만
                                     *    「아직 집계되지 않는 항목」이라고 남아 있었다.
                                     *    장애인데 기다리면 채워질 값으로 읽힌다.
                                     */
                                    x.v != null
                                        ? undefined
                                        : funnelBroken
                                          ? '이 달 전환 집계가 실패했습니다. 0건이 아니니 잠시 후 다시 불러와 주세요'
                                          : '아직 집계되지 않는 항목입니다'
                                }
                            >
                                {x.v == null ? '—' : x.v}
                            </span>
                            {x.v != null && <span style={{ fontSize: 12, color: '#8FA5C4' }}>건</span>}
                        </span>
                    ))}
                    <span
                        className="ml-auto rounded-[8px]"
                        style={{
                            /*
                              기준선도 「라포 평균」과 **같은 값**이어야 한다.
                              예전에는 여기만 32 로 박혀 있어서, 평균 문구가 갱신되면
                              "평균 52.9% 인데 40% 가 초록" 같은 화면이 됐다.
                            */
                            background:
                                convPct == null
                                    ? 'rgba(255,255,255,0.10)'
                                    : convPct >= FIRST_PASS_REREGISTRATION_RATE
                                      ? 'rgba(0,167,147,0.25)'
                                      : 'rgba(255,62,74,0.22)',
                            color:
                                convPct == null
                                    ? '#B7C7E0'
                                    : convPct >= FIRST_PASS_REREGISTRATION_RATE
                                      ? '#8FE3CD'
                                      : '#FFB0B6',
                            fontSize: 14,
                            fontWeight: 800,
                            padding: '6px 14px',
                        }}
                    >
                        {/*
                          ⚠️ 숫자를 직접 적지 않는다. 여기 '31.7%' 가 박혀 있었는데,
                             그 값은 폐기된 이탈률 68.3 의 여집합이라 실측이 갱신된
                             뒤에도 옛 숫자를 계속 말했다 (passOrder.ts:194).
                             원천은 CHURN_RATE_BY_ORDER 하나다.
                        */}
                        {funnelBroken
                            ? '재등록 전환율 집계 실패 — 다시 불러와 주세요'
                            : convPct == null
                              ? `재등록 전환율 집계 전 · 라포 평균 ${FIRST_PASS_REREGISTRATION_RATE}%`
                              : `재등록 전환율 ${convPct}% · 라포 평균 ${FIRST_PASS_REREGISTRATION_RATE}%`}
                    </span>
                </div>
            </div>

            {/*
              단계 선택.
              이전에는 평평한 카드 네 장이라 눌러도 되는지 몰랐다.
              그래서 (1) 마우스를 올리면 떠오르고 (2) 고른 것은 아래 패널과 붙어 보이게 하고
              (3) "보는 중" 표시와 화살표로 지금 무엇을 보고 있는지 못 놓치게 한다.
            */}
            <div>
                <div className="mb-[10px] flex items-baseline" style={{ gap: 10 }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: '#16202E' }}>
                        어느 단계를 보시겠어요?
                    </span>
                    <span style={{ fontSize: 13.5, color: '#93A0AE' }}>
                        아래 4개 중 하나를 눌러보세요
                    </span>
                    {/*
                      🔴 겹친 회원 수를 밝힌다. 안 밝히면 원장님이 네 칸의 인원을
                         더해 보고 「관리 중인 회원 N명」과 안 맞는 것을 발견한다.
                         금액은 안 겹친다 — 선수금은 수강권마다 따로고, 기대는 대표
                         수강권 한 곳에만 들어간다. 겹치는 것은 **인원뿐**이다.
                    */}
                    {multiStageMembers > 0 && (
                        <span
                            className="ml-auto"
                            title={
                                '수강권이 여러 건인 회원은 수강권마다 단계가 달라 두 칸에 함께 섭니다.\n' +
                                '그래서 네 칸의 인원을 더하면 전체 회원 수보다 많습니다.\n\n' +
                                '금액은 겹치지 않습니다 — 미사용 선수금은 수강권마다 따로 받은 돈이고, ' +
                                '재등록 기대는 대표 수강권 한 곳에서만 셉니다.'
                            }
                            style={{ fontSize: 12.5, color: '#A7B0BC', cursor: 'help' }}
                        >
                            ※ 두 단계에 함께 선 회원 {multiStageMembers}명 — 인원을 더하면 전체보다 많습니다
                        </span>
                    )}
                </div>
                <div className="flex" style={{ gap: 10 }}>
                    {STAGES.map((st, i) => {
                        const m = STAGE_META[st];
                        const active = st === stage;
                        const hot = hoverStage === st;
                        const n = countOf(st);
                        /*
                          줄 수(=수강권 건수)와 인원이 다를 수 있다.
                          서버는 활성 수강권 한 건당 카드 한 장을 주므로, 수강권이 둘인
                          회원은 목록에 줄이 둘이다. 큰 숫자는 **회원 수**로 두고,
                          다를 때만 건수를 작게 병기한다 — 안 적으면 원장님이
                          "3명이라더니 줄이 4개" 라고 읽는다.
                        */
                        const cards = members.filter((x) => x.stage === st).length;
                        return (
                            /*
                              🔴 겉껍데기가 `div` 다 — 예전에는 카드 전체가 `button` 이었다
                                 (2026-09-01 QA31).

                              금액에도 「왜 이 금액인가」 버튼이 붙으면서 **단추 안에 단추**가
                              생겼다. 브라우저가 그 조합을 어떻게 다루는지에 기대고 싶지 않고,
                              보조기기에서는 무엇이 눌리는지 알 수 없다.

                              🔴 그런데 QA31 에서 「위쪽만 단추」로 갈랐더니 **카드
                                 본문을 눌러도 아무 일이 없었다** (2026-09-02 대표 QA ④).
                                 원장님은 카드 가운데를 누른다 — 거기가 죽어 있으면
                                 「클릭이 안 된다」가 된다. 실제로 그렇게 보고됐다.

                              그래서 **상자 전체가 다시 눌린다.** 단추 안에 단추를
                              만들지 않으려고 상자는 `div` + `role="button"` 이고,
                              안쪽 금액 단추는 `stopPropagation` 으로 자기 일만 한다.
                              키보드도 그대로다 — Enter·Space 로 고를 수 있다.
                            */
                            <div
                                key={st}
                                role="button"
                                tabIndex={0}
                                aria-pressed={active}
                                aria-label={`${m.label} 단계 보기`}
                                onClick={() => setPicked(st)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setPicked(st);
                                    }
                                }}
                                onMouseEnter={() => setHoverStage(st)}
                                onMouseLeave={() => setHoverStage(null)}
                                className="relative flex flex-1 flex-col rounded-[13px]"
                                style={{
                                    cursor: 'pointer',
                                    border: `2px solid ${active ? m.dot : hot ? m.dot : '#E9EDF4'}`,
                                    background: active ? m.bg : hot ? '#FBFCFE' : '#FFFFFF',
                                    boxShadow: active
                                        ? `0 8px 20px ${m.dot}33`
                                        : hot
                                          ? '0 6px 16px rgba(16,32,64,0.10)'
                                          : '0 1px 2px rgba(16,32,64,0.04)',
                                    textAlign: 'left',
                                    minWidth: 0,
                                    transform: hot && !active ? 'translateY(-3px)' : 'translateY(0)',
                                    transition: 'all .16s ease',
                                }}
                            >
                            <div
                                className="flex w-full flex-col"
                                style={{
                                    padding: '15px 17px 0',
                                    textAlign: 'left',
                                    minWidth: 0,
                                }}
                            >
                                {/*
                                  🔴 카드 다섯 장이 한 줄에 들어가는 폭이라 **글자가 넘치면
                                     옆 카드를 침범한다** (2026-09-01 QA30).

                                  예전에는 제목이 `flex: none` 이라 줄어들지 않았고, 오른쪽
                                  인원 뭉치는 `flex` 기본값이라 「· 수강권 47건」이 「수강권」과
                                  「47건」 사이에서 접혔다. 「재등록 골든타임」이 가장 긴 제목이라
                                  그 카드에서 먼저 터졌고, 실제로 「45명 · 수강권 47건」이
                                  카드 밖으로 밀려 나가 겹쳐 보였다.

                                  규칙 셋을 둔다.
                                    ① 인원 뭉치는 **절대 안 접힌다** (`nowrap` · `flex: none`).
                                       숫자와 단위가 갈라지면 읽을 수 없다.
                                    ② 제목은 **줄어들고 필요하면 접힌다**. 한글은 `keep-all`
                                       이라 「재등록 / 골든타임」처럼 어절에서만 나뉜다.
                                       그래도 모자라면 `anywhere` 가 마지막으로 막는다 —
                                       못생겨도 카드 밖으로 나가는 것보다 낫다.
                                    ③ 머리말 높이를 **두 줄로 고정**한다. 안 그러면 제목이
                                       접힌 카드만 아래 내용이 밀려 다섯 장의 줄이 어긋난다.
                                */}
                                <div
                                    className="flex items-center"
                                    style={{ gap: 7, minHeight: 42 }}
                                >
                                    <span
                                        className="flex items-center justify-center rounded-full"
                                        style={{
                                            width: 22,
                                            height: 22,
                                            background: active || hot ? m.dot : '#EDF1F7',
                                            color: active || hot ? '#FFFFFF' : '#93A0AE',
                                            fontSize: 12,
                                            fontWeight: 800,
                                            flex: 'none',
                                            transition: 'background .16s',
                                        }}
                                    >
                                        {st === 'AT_RISK' ? '!' : i + 1}
                                    </span>
                                    <span
                                        title={m.label}
                                        style={{
                                            fontSize: 15.5,
                                            fontWeight: 700,
                                            color: active ? '#16202E' : '#5B6B7F',
                                            letterSpacing: '-0.01em',
                                            lineHeight: '21px',
                                            flex: '1 1 auto',
                                            minWidth: 0,
                                            wordBreak: 'keep-all',
                                            overflowWrap: 'anywhere',
                                        }}
                                    >
                                        {m.label}
                                    </span>
                                    <span
                                        className="ml-auto flex items-baseline"
                                        style={{ gap: 1, flex: 'none', whiteSpace: 'nowrap' }}
                                    >
                                        <span
                                            style={{
                                                fontSize: 22,
                                                fontWeight: 800,
                                                color: active ? m.accent : '#7C8794',
                                                letterSpacing: '-0.03em',
                                            }}
                                        >
                                            {n}
                                        </span>
                                        <span style={{ fontSize: 12.5, fontWeight: 600, color: '#93A0AE' }}>
                                            명
                                        </span>
                                        {cards !== n && (
                                            <span
                                                title={`활성 수강권 ${cards}건 — 수강권이 둘 이상인 회원은 목록에 줄이 여러 개입니다`}
                                                style={{
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    color: '#A7B0BC',
                                                    marginLeft: 3,
                                                    cursor: 'help',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                · 수강권 {cards}건
                                            </span>
                                        )}
                                    </span>
                                </div>

                                <span
                                    style={{
                                        fontSize: 12,
                                        color: '#8B96A3',
                                        marginTop: 8,
                                        lineHeight: '17px',
                                        minHeight: 34,
                                        /* 한글은 어절에서만 나뉜다 — 「집중 / 관리 구간」 */
                                        wordBreak: 'keep-all',
                                    }}
                                >
                                    {m.sub}
                                </span>
                            </div>

                                {/* 하단 — 눌렀는지 안 눌렀는지가 여기서 갈린다 */}
                                <div
                                    className="flex flex-col"
                                    style={{
                                        margin: '8px 17px 17px',
                                        paddingTop: 9,
                                        borderTop: `1px solid ${active ? m.border : '#F1F4F9'}`,
                                        gap: 5,
                                    }}
                                >
                                    {/*
                                      🔴 두 숫자를 나란히 (정책 M-D3).
                                         한 숫자만 두면 그 숫자의 뜻이 단계마다 달라진다 —
                                         예전에 정확히 그랬고, 그래서 네 칸을 비교할 수 없었다.
                                         「선수금」은 이미 받은 돈, 「기대」는 확률을 곱한 기댓값이라
                                         **더하지 않는다.** 가운데 점으로 갈라만 놓는다.

                                         칸 넷이 한 줄에 들어가야 해서 금액 줄과 버튼 줄을 위아래로
                                         나눴다. 한 줄에 다 넣으면 「선수금 1.2억 · 기대 4천만」 뒤에
                                         「눌러서 보기」가 밀려 카드마다 줄바꿈 위치가 달라진다.

                                      🔴 두 금액이 **각각 눌러지는 자리**가 됐다 (QA31).
                                         이 단계의 어느 회원이 얼마씩 들어갔는지를 편다.
                                         선수금과 기대는 분모가 달라(카드별 · 회원별) 팝업도
                                         따로 연다 — 한 창에 섞으면 원장님이 더해 버린다.
                                    */}
                                    <span
                                        className="flex items-baseline"
                                        style={{
                                            fontSize: 13,
                                            fontWeight: 700,
                                            color: active ? m.accent : '#7C8794',
                                            gap: 5,
                                            minWidth: 0,
                                            /*
                                              좁은 카드에서 두 금액이 한 줄에 안 들어가면
                                              **아래로 내려가야 한다.** 예전에는 `nowrap` 조각
                                              둘이 카드 밖으로 밀려 나갔다 (QA30).
                                            */
                                            flexWrap: 'wrap',
                                            rowGap: 2,
                                        }}
                                    >
                                        <ValueTrigger
                                            label={`${m.label} 단계 미사용 선수금 산출 근거 보기`}
                                            /* 상자 전체가 단계 고르기라, 여기서 멈춰 세운다 */
                                            stopPropagation
                                            onClick={() => setValueModal({ kind: 'PREPAID_STAGE', stage: st })}
                                            style={{ whiteSpace: 'nowrap' }}
                                        >
                                            <span style={{ fontSize: 10.5, fontWeight: 600, opacity: 0.75 }}>
                                                선수금{' '}
                                            </span>
                                            {shortWon(prepaidOf(st))}
                                        </ValueTrigger>
                                        <span style={{ opacity: 0.35, fontWeight: 400 }}>·</span>
                                        <ValueTrigger
                                            label={`${m.label} 단계 재등록 기대 산출 근거 보기`}
                                            stopPropagation
                                            onClick={() => setValueModal({ kind: 'EXPECTED_STAGE', stage: st })}
                                            style={{ whiteSpace: 'nowrap' }}
                                        >
                                            <span style={{ fontSize: 10.5, fontWeight: 600, opacity: 0.75 }}>
                                                기대{' '}
                                            </span>
                                            {shortWon(expectedOf(st))}
                                        </ValueTrigger>
                                    </span>
                                    <span
                                        className="flex items-center"
                                        style={{ gap: 6, flexWrap: 'wrap', rowGap: 4 }}
                                    >
                                    {unpricedOf(st) > 0 && (
                                        <span
                                            title="단가나 결제 금액이 없어 금액을 계산하지 않은 회원입니다. 0원이라는 뜻이 아닙니다."
                                            style={{
                                                fontSize: 11,
                                                fontWeight: 600,
                                                color: '#A7B0BC',
                                                whiteSpace: 'nowrap',
                                                cursor: 'help',
                                            }}
                                        >
                                            +{unpricedOf(st)}명 산정 불가
                                        </span>
                                    )}
                                    {/*
                                      단계를 고르는 두 번째 자리. 겉껍데기가 더 이상 단추가
                                      아니라(위 주석) 이 배지 자체가 눌려야 한다 — 안 그러면
                                      「눌러서 보기」라고 적힌 곳이 안 눌린다.
                                    */}
                                    {/*
                                      상자 전체가 눌리므로 이 배지는 **표시**다.
                                      중첩 단추를 없애 보조기기가 하나만 읽게 한다.
                                    */}
                                    <span
                                        className="ml-auto flex items-center rounded-[6px]"
                                        style={{
                                            background: active ? m.dot : hot ? m.bg : 'transparent',
                                            color: active ? '#FFFFFF' : hot ? m.accent : '#A7B0BC',
                                            fontSize: 12,
                                            fontWeight: 700,
                                            padding: active || hot ? '3px 9px' : '3px 0',
                                            gap: 4,
                                            cursor: 'pointer',
                                            transition: 'all .16s',
                                        }}
                                    >
                                        {active ? '보는 중' : '눌러서 보기'}
                                        {!active && <span style={{ fontSize: 13 }}>›</span>}
                                    </span>
                                    </span>
                                </div>

                                {/* 선택된 탭을 아래 패널과 잇는 꼬리 */}
                                {active && (
                                    <span
                                        style={{
                                            position: 'absolute',
                                            left: '50%',
                                            bottom: -8,
                                            width: 14,
                                            height: 14,
                                            marginLeft: -7,
                                            background: m.bg,
                                            borderRight: `2px solid ${m.dot}`,
                                            borderBottom: `2px solid ${m.dot}`,
                                            transform: 'rotate(45deg)',
                                        }}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
                {/*
                  🔴 「기대」 숫자가 어디서 왔는지를 **눈에 보이게** 적는다 (정책 M5).
                     마우스오버로만 두면 원장님은 그 금액을 시세나 추정 단가로 읽는다.
                     예상액은 직전 결제액 그대로이고, 거기에 순번별 실측 요율만 곱한다.
                     이탈 위험·주의 칸의 기대가 0인 것도 여기서 함께 밝힌다 —
                     안 밝히면 「그 회원들은 돈이 안 걸린 사람」으로 읽힌다.
                */}
                <span
                    style={{
                        display: 'block',
                        marginTop: 9,
                        fontSize: 12,
                        color: '#93A0AE',
                        lineHeight: '17px',
                    }}
                >
                    ※ 「선수금」은 잔여 회차 × 회당 단가로 이미 받아 둔 돈이고, 「기대」는 다음 수강권
                    예상액 × 재등록률입니다. 성격이 달라 더하지 않습니다.
                    {' '}다음 수강권 예상액은 <b style={{ color: '#7C8794' }}>직전과 같은 금액으로
                    보수적으로</b> 잡았습니다. 이탈 위험·주의 단계는 재등록을 기대하지 않아 기대가 0입니다.
                </span>
            </div>

            {/* 선택한 단계 — 가이드가 명단보다 먼저 온다 */}
            <div
                className="w-full overflow-hidden rounded-[16px] border border-solid"
                style={{ borderColor: meta.border, background: '#FFFFFF' }}
            >
                {/* 단계 머리말 — 임팩트 한 줄이 먼저 */}
                <div style={{ background: meta.bg, padding: '22px 26px', borderBottom: `1px solid ${meta.border}` }}>
                    {/* 좁은 폭에서 조각이 접히더라도 숫자와 단위는 붙어 있어야 한다 (QA30) */}
                    <div
                        className="flex items-center"
                        style={{ gap: 9, flexWrap: 'wrap', rowGap: 4 }}
                    >
                        <span
                            style={{
                                width: 10,
                                height: 10,
                                borderRadius: 5,
                                background: meta.dot,
                                flex: 'none',
                            }}
                        />
                        <span
                            style={{
                                fontSize: 15,
                                fontWeight: 700,
                                color: meta.accent,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {meta.label}
                        </span>
                        {/*
                          "(N명 산정 불가)" 를 여기서 뺐다. 지운 게 아니라, 바로 위
                          단계 카드가 같은 값을 이미 적고 있다 — 눌러서 연 칸 안에서
                          한 번 더 반복하면 두 곳 다 안 읽힌다.
                          (전체 합계에서 몇 명이 빠졌는지는 파이프라인 머리말이 맡는다)
                        */}
                        <span
                            className="flex items-baseline"
                            style={{
                                fontSize: 14,
                                color: '#7C8794',
                                gap: 6,
                                flexWrap: 'wrap',
                                rowGap: 2,
                                minWidth: 0,
                            }}
                        >
                            {/* 「N명」은 회원 수다 — 줄 수가 아니다 */}
                            <span style={{ whiteSpace: 'nowrap' }}>회원 {countOf(stage)}명</span>
                            {members.filter((x) => x.stage === stage).length !== countOf(stage) && (
                                <span style={{ whiteSpace: 'nowrap' }}>
                                    · 수강권 {members.filter((x) => x.stage === stage).length}건
                                </span>
                            )}
                            {/* 위 카드와 같은 두 숫자 — 한 숫자로 합치면 뜻이 흐려진다 */}
                            <span style={{ whiteSpace: 'nowrap' }}>
                                · 선수금 {shortWon(prepaidOf(stage))}
                            </span>
                            <span style={{ whiteSpace: 'nowrap' }}>
                                · 기대 {shortWon(expectedOf(stage))}
                            </span>
                        </span>
                    </div>
                    {/*
                      🔴 부연 설명(`guide.who`)을 **본문에서 뺐다** (2026-08-31 QA13).
                         대표 지시 — 「아래 설명 글은 최대한 요약하고 간결하게만」.
                         숫자와 행동만 남기고 산문을 없앤다. 원장이 아침에 쓰는
                         30초 안에 읽히려면 문단이 아니라 헤드라인·차트·할 일이어야 한다.

                         ⚠️ **지우지 않고 툴팁으로 옮겼다.** 「이 단계가 무슨 단계인가」는
                            처음 보는 사람에게는 필요한 정보라, 궁금할 때 볼 수 있게 남긴다.
                    */}
                    <div style={{ marginTop: 9 }}>
                        <span
                            title={guide.who}
                            style={{
                                fontSize: 27,
                                fontWeight: 800,
                                color: '#16202E',
                                letterSpacing: '-0.035em',
                                lineHeight: '36px',
                                cursor: 'help',
                            }}
                        >
                            {guide.headline}
                        </span>
                    </div>
                </div>

                {/* 근거는 막대로, 할 일은 목록으로 */}
                <div className="flex" style={{ borderBottom: `1px solid ${BORDER}` }}>
                    <div style={{ flex: 1, padding: '20px 26px', borderRight: `1px solid ${BORDER}` }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: '#16202E' }}>
                            {guide.stat.title}
                        </span>

                        <div className="flex flex-col" style={{ gap: 10, marginTop: 14 }}>
                            {guide.stat.bars.map((b) => {
                                const max = Math.max(...guide.stat.bars.map((x: { value: number }) => x.value)) * 1.1;
                                const color =
                                    b.tone === 'BAD' ? '#FF5A64' : b.tone === 'GOOD' ? '#00A793' : '#A8B6CA';
                                const mine = b.label.includes('이 단계');
                                return (
                                    <div key={b.label} className="flex items-center" style={{ gap: 10 }}>
                                        <span
                                            style={{
                                                fontSize: 12.5,
                                                color: mine ? '#16202E' : '#7C8794',
                                                fontWeight: mine ? 700 : 400,
                                                width: 108,
                                                flex: 'none',
                                                textAlign: 'right',
                                            }}
                                        >
                                            {b.label}
                                        </span>
                                        <div style={{ flex: 1, position: 'relative', height: 28 }}>
                                            <div
                                                style={{
                                                    width: `${(b.value / max) * 100}%`,
                                                    height: 28,
                                                    background: color,
                                                    borderRadius: 6,
                                                    minWidth: 6,
                                                    boxShadow: mine ? `0 2px 8px ${color}55` : 'none',
                                                }}
                                            />
                                            <span
                                                style={{
                                                    position: 'absolute',
                                                    left: `calc(${(b.value / max) * 100}% + 9px)`,
                                                    top: 3,
                                                    fontSize: 17,
                                                    fontWeight: 800,
                                                    color: mine ? '#16202E' : '#7C8794',
                                                    letterSpacing: '-0.02em',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {b.value}
                                                <span style={{ fontSize: 12.5, fontWeight: 600 }}>
                                                    {guide.stat.unit}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div
                            className="rounded-[9px]"
                            style={{ marginTop: 14, background: '#F7F9FC', padding: '11px 13px' }}
                        >
                            <span style={{ fontSize: 13.5, color: '#3A4757', lineHeight: '20px' }}>
                                👉 {guide.stat.caption}
                            </span>
                        </div>
                    </div>

                    <div style={{ flex: 1.05, padding: '20px 26px' }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: '#16202E' }}>
                            이 단계에서 할 일
                        </span>
                        <div className="flex flex-col" style={{ gap: 9, marginTop: 14 }}>
                            {guide.steps.map((st) => (
                                /*
                                  라벨 + 한 줄로 압축했다 (QA13). 세 칸에 두 줄씩
                                  쌓이면 아홉 줄이 되고, 그러면 아무도 안 읽는다.
                                  근거 문장(detail)은 툴팁으로 남긴다.
                                */
                                <div
                                    key={st.title}
                                    title={st.detail}
                                    className="flex items-center rounded-[10px]"
                                    style={{ border: `1px solid ${BORDER}`, padding: '11px 13px', gap: 10, cursor: 'help' }}
                                >
                                    <span
                                        className="flex flex-none items-center justify-center rounded-[6px]"
                                        style={{
                                            background: '#1A305D',
                                            color: '#FFFFFF',
                                            fontSize: 11.5,
                                            fontWeight: 800,
                                            padding: '4px 8px',
                                            marginTop: 1,
                                            minWidth: 58,
                                            textAlign: 'center',
                                        }}
                                    >
                                        {st.verb}
                                    </span>
                                    <span style={{ fontSize: 14.5, fontWeight: 700, color: '#16202E', lineHeight: '21px' }}>
                                        {st.title}
                                    </span>
                                </div>
                            ))}
                        </div>
                        {guide.caution && (
                            <div
                                className="rounded-[9px]"
                                style={{
                                    marginTop: 11,
                                    background: '#FEF3C7',
                                    padding: '11px 13px',
                                    borderLeft: '3px solid #F0A020',
                                }}
                            >
                                <span style={{ fontSize: 13.5, color: '#8A5A00', lineHeight: '20px' }}>
                                    ⚠️ {guide.caution}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* 명단 */}
                <div
                    className="flex items-center justify-between"
                    style={{ padding: '15px 26px', borderBottom: `1px solid ${BORDER}`, background: '#FCFDFE' }}
                >
                    <div className="flex items-baseline" style={{ gap: 9 }}>
                        {/*
                          줄 수와 인원이 다를 수 있다 — 활성 수강권이 둘인 회원은
                          줄이 둘이다. 줄 수를 「N명」이라 부르면 회원 명부와 안 맞는다.
                          다를 때만 건수를 함께 적는다(늘 적으면 눈만 어지럽다).
                        */}
                        <span style={{ fontSize: 16.5, fontWeight: 700, color: '#16202E' }}>
                            이 단계 회원 {distinctMembers(list)}명
                            {distinctMembers(list) !== list.length && (
                                <span style={{ fontWeight: 600, color: '#5B6B7F' }}>
                                    {' '}
                                    · 수강권 {list.length}건
                                </span>
                            )}
                        </span>
                        {/* 정렬 기준은 단계마다 다르다. 화면이 뭘로 줄 세웠는지 말해 줘야 목록을 믿는다 */}
                        <span style={{ fontSize: 13, color: '#93A0AE' }}>
                            {stage === 'GOLDEN'
                                ? '마지막 수업이 최신인 순서로 정렬했습니다 · 최근까지 나온 분이 재등록 가능성이 높습니다'
                                : '급한 순서로 정렬했습니다'}{' '}
                            · 줄을 누르면 근거와 추천 문구가 열립니다
                        </span>
                    </div>
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="회원 · 강사 이름"
                        style={{
                            height: 36,
                            width: 200,
                            padding: '0 12px',
                            fontSize: 14,
                            border: `1px solid ${BORDER}`,
                            borderRadius: 9,
                            background: '#FFFFFF',
                            color: '#16202E',
                        }}
                    />
                </div>

                {list.length === 0 ? (
                    <div style={{ padding: '48px 0', textAlign: 'center' }}>
                        <span style={{ fontSize: 16, color: '#93A0AE' }}>
                            {q.trim()
                                ? `‘${q.trim()}’ 과(와) 맞는 회원이 이 단계에 없습니다`
                                : members.length === 0 && hiddenMembers.length > 0
                                  ? /*
                                       전부 가려 둔 상태. "회원이 없습니다" 라고만 하면
                                       원장님은 데이터가 사라진 줄 안다 — 아래 접이식
                                       목록에서 되돌릴 수 있다는 것을 여기서 말한다.
                                     */
                                    `관리 중인 회원 ${hiddenMembers.length}명을 모두 가려 두셨습니다. 아래에서 다시 보실 수 있습니다`
                                  : '이 단계에 해당하는 회원이 없습니다'}
                        </span>
                    </div>
                ) : (
                    /*
                      좁은 창에서 열을 찌그러뜨리지 않는다.
                      숫자 칸이 줄바꿈되면 위아래 비교라는 이 목록의 존재 이유가 사라진다.
                    */
                    <div style={{ overflowX: 'auto' }}>
                        <div style={{ minWidth: summaryMinWidth(caps) }}>
                            <MemberSummaryHeader {...caps} />
                            {list.map((m, i) => (
                                <MemberSummaryRow
                                    /* 카드 단위 키 — memberId 는 한 회원에 여러 줄이라 겹친다 */
                                    key={m.cardKey}
                                    m={m}
                                    stage={stage}
                                    /* 머리글과 **같은 값**을 넘긴다 — 갈리면 숫자가 라벨과 밀린다 */
                                    caps={caps}
                                    first={i === 0}
                                    selected={m.cardKey === openId}
                                    /*
                                      이미 나가 있는 지시. 토스트는 사라지므로
                                      "보냈다"는 사실이 목록에 남아 있어야 한다.
                                    */
                                    sentOrder={confirm.sentOrderFor(m.memberId)}
                                    // 확인 창에서 전송 중이면 목록 전체를 잠근다
                                    locked={confirm.sending}
                                    onOpen={setOpenId}
                                    // 화면이 보여준 진단 문장을 그대로 지시로 싣는다 (handleOrder 주석 참고)
                                    onOrder={(target) => handleOrder(target, diagnose(target, stage))}
                                    onHide={handleHide}
                                    /*
                                      가리기 요청이 하나라도 나가 있으면 **모든 줄**을 잠근다.
                                      서버는 반대 방향 동시 요청에서 마지막 write 가 이기고 제출
                                      순서를 보장하지 않는다. 다른 줄만 열어 두면 눌러도 아무 일이
                                      없는 버튼(아래 handleHide 의 조기 반환)이 되어 더 나쁘다.
                                    */
                                    hiding={pendingHideId != null}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/*
                  가린 회원.

                  목록에서는 빠지되 **없던 일이 되지는 않는다.** 되돌리기 토스트는
                  몇 초 뒤 사라지므로, 그것만으로는 원장님이 자기가 누구를 가렸는지
                  다시 볼 방법이 없다. 서버에 남아 있는 사실이라 화면에도 꺼내 볼
                  길을 둔다 (오늘의 브리핑 「가린 항목 N건 보기」와 같은 생각).

                  ⚠️ 이 목록은 **단계와 검색어를 타지 않는다.** 가리기는 회원 단위·영구라
                     "이 단계에서만 가려졌다"는 것이 없다. 단계를 옮겨 다니며 찾게 하면
                     되돌릴 길이 사실상 막힌다.
                */}
                {hiddenMembers.length > 0 && (
                    <div style={{ padding: '14px 26px', borderTop: `1px solid ${BORDER}` }}>
                        <button
                            type="button"
                            onClick={() => setShowHidden((v) => !v)}
                            aria-expanded={showHidden}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                fontSize: 13.5,
                                color: '#93A0AE',
                                textDecoration: 'underline',
                                textUnderlineOffset: 3,
                                padding: 0,
                            }}
                        >
                            가린 회원 {hiddenMembers.length}명 {showHidden ? '접기' : '보기'}
                        </button>
                        {showHidden && (
                            <div
                                className="w-full rounded-[10px] border border-solid"
                                style={{
                                    borderColor: BORDER,
                                    background: '#FBFCFE',
                                    marginTop: 8,
                                    padding: '6px 14px',
                                }}
                            >
                                {hiddenMembers.map((h) => (
                                    <div
                                        key={h.memberId}
                                        className="flex items-center justify-between"
                                        style={{ padding: '9px 0', gap: 12 }}
                                    >
                                        <span
                                            style={{
                                                fontSize: 13.5,
                                                color: '#7C8794',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {h.name} · {h.coachName} 강사 · {h.product}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => handleUnhide(h)}
                                            disabled={pendingHideId != null}
                                            aria-label={`${h.name} 회원 다시 보기`}
                                            style={{
                                                flex: 'none',
                                                border: 'none',
                                                background: 'transparent',
                                                cursor: pendingHideId != null ? 'progress' : 'pointer',
                                                fontSize: 13,
                                                color: '#3D73DD',
                                                textDecoration: 'underline',
                                                textUnderlineOffset: 3,
                                                padding: 0,
                                            }}
                                        >
                                            다시 보기
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/*
              상세 — 목록이 원래 쓰던 카드를 그대로 담는다.
              여기서 다시 그리면 목록을 열었을 때와 안 열었을 때가 갈린다.
            */}
            {opened && (
                <MemberDetailDrawer
                    member={opened}
                    stage={opened.stage}
                    onClose={() => setOpenId(null)}
                    /*
                      확인 창이 위에 떠 있으면 Esc 는 그 창만 닫아야 한다.
                      둘 다 닫히면 취소를 눌렀을 뿐인데 보던 회원까지 잃는다.
                      금액 산출 근거 팝업도 같다 — 이 상세 **안에서** 열린다.
                    */
                    escapeLocked={confirm.modal != null || valueModal != null}
                >
                    <MemberRow
                        m={opened}
                        stage={opened.stage}
                        first
                        daysAgo={daysAgo}
                        onOrder={handleOrder}
                        onGift={handleGift}
                        /* 선물만 조건이 다르다 — 포인트가 나간다 (MemberRow 의 버튼 주석 참고) */
                        giftHidden={!gift.allowed}
                        giftDisabled={!isToday}
                        giftDisabledReason="지난 날짜에서는 선물을 보낼 수 없습니다"
                        sentGift={gift.sentGiftFor(opened.memberId)}
                        sentOrder={confirm.sentOrderFor(opened.memberId)}
                        locked={confirm.sending || gift.sending}
                        /* 목록과 같은 판정을 넘긴다 — 열었더니 없던 칸이 생기면 안 된다 */
                        showSameDay={caps.showSameDay}
                        siblings={openedSiblings}
                        /* 금액 배지를 누르면 이 수강권 한 건의 산출 근거를 편다 (QA31) */
                        onOpenValue={(axis) =>
                            setValueModal({ kind: 'CARD', cardKey: opened.cardKey, axis })
                        }
                    />
                </MemberDetailDrawer>
            )}

            {/* 왜 이 금액인가 — 헤더 · 단계 카드 · 카드 배지가 모두 이 창을 연다 */}
            {valueModalNode}

            {/* 지시 보내기 전 확인 — 오늘의 브리핑과 같은 창이다 */}
            {confirm.modal}

            {/* 선물 보내기 — 오늘의 브리핑과 같은 창이고, 2차 비밀번호 창까지 이 안에 있다 */}
            {gift.modal}

            {toast && (
                <div
                    className="fixed left-1/2 flex items-center rounded-[10px]"
                    style={{
                        bottom: 40,
                        transform: 'translateX(-50%)',
                        background: '#1A305D',
                        color: '#FFFFFF',
                        fontSize: 15.5,
                        padding: '14px 22px',
                        gap: 16,
                        zIndex: 1000,
                        boxShadow: '0 8px 24px rgba(26,48,93,0.24)',
                    }}
                >
                    <span>{toast.text}</span>
                    {/*
                      되돌릴 수 있는 동작은 **그 자리에서** 되돌린다.
                      되돌릴 길을 목록 아래로만 두면, 잘못 눌렀다는 것을 알아챈
                      순간 어디를 눌러야 하는지 모른다 (오늘의 브리핑과 같은 규칙).
                    */}
                    {toast.undo && (
                        <button
                            type="button"
                            onClick={() => {
                                const run = toast.undo?.run;
                                setToast(null);
                                run?.();
                            }}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                color: '#9DC2FF',
                                fontSize: 15,
                                fontWeight: 700,
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                textUnderlineOffset: 3,
                                padding: 0,
                                flex: 'none',
                            }}
                        >
                            {toast.undo.label}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

/* ───────────────────────── 회원 한 줄 ───────────────────────── */

/**
 * 단계마다 봐야 할 지표가 다르다.
 * 여섯 칸을 똑같은 크기로 늘어놓으니 "뭘 보라는 거지"가 됐다.
 * 그래서 단계별로 핵심 하나를 정하고 그 칸만 크게 세운다.
 */
/** 공백 일수를 사람 말로 */
const gapHint = (days: number) => {
    if (days <= 3) return '최근에 왔습니다';
    if (days <= 7) return `${days}일째 공백`;
    if (days <= 13) return '일주일 넘게 공백';
    return '2주 넘게 공백';
};

const KEY_METRIC: Record<MemberStage, string> = {
    FIRST_PASS: '주당 수업',
    STABLE: '주당 수업',
    /* 주의는 「얼마나 오는가」가 판단의 축이다 — 네 이유 중 둘이 이 값에 걸린다 */
    WATCH: '주당 수업',
    GOLDEN: '남은 수업',
    AT_RISK: '마지막 수업',
};

/** 지표 한 칸 — 숫자만 있으면 무슨 뜻인지 모른다. 라벨과 해석을 같이 둔다 */
const Cell = ({
    label,
    value,
    unit,
    hint,
    alert,
    primary,
    title,
}: {
    label: string;
    value: string;
    unit?: string;
    hint?: string;
    alert?: boolean;
    /** 이 단계의 핵심 지표인가 */
    primary?: boolean;
    /** 마우스를 올렸을 때 보여줄 원 수치·근거 */
    title?: string;
}) => (
    <div
        className="flex flex-col rounded-[10px]"
        title={title}
        style={{
            flex: primary ? 1.5 : 1,
            minWidth: 0,
            padding: primary ? '11px 14px' : '11px 12px',
            background: primary ? (alert ? '#FFF0F0' : '#F1F5FC') : 'transparent',
            border: primary ? `1px solid ${alert ? '#FBD0D3' : '#D3E0F5'}` : '1px solid transparent',
            cursor: title ? 'help' : 'inherit',
        }}
    >
        <div className="flex items-center" style={{ gap: 5 }}>
            <span style={{ fontSize: 12, color: primary ? (alert ? '#C22D38' : '#2C4C8C') : '#93A0AE', fontWeight: primary ? 700 : 400 }}>
                {label}
            </span>
            {primary && (
                <span
                    className="rounded-[4px]"
                    style={{
                        background: alert ? '#C22D38' : '#2C4C8C',
                        color: '#FFFFFF',
                        fontSize: 9.5,
                        fontWeight: 800,
                        padding: '2px 5px',
                        letterSpacing: '0.02em',
                    }}
                >
                    핵심
                </span>
            )}
        </div>
        <div className="flex items-baseline" style={{ gap: 2, marginTop: 4 }}>
            <span
                style={{
                    fontSize: primary ? 28 : 19,
                    fontWeight: 800,
                    color: alert ? '#C22D38' : '#16202E',
                    letterSpacing: '-0.03em',
                    lineHeight: primary ? '32px' : '24px',
                }}
            >
                {value}
            </span>
            {unit && (
                <span style={{ fontSize: primary ? 14 : 12, fontWeight: 600, color: alert ? '#D9737B' : '#7C8794' }}>
                    {unit}
                </span>
            )}
        </div>
        {hint && (
            <span
                style={{
                    fontSize: primary ? 12.5 : 11.5,
                    color: alert ? '#D9737B' : '#A7B0BC',
                    marginTop: 3,
                    lineHeight: '16px',
                }}
            >
                {hint}
            </span>
        )}
    </div>
);

/** 8주 추이 — 최근 3주만 색을 넣어 하락이 눈에 띄게 */
const Spark = ({ data, down }: { data: number[]; down: boolean }) => {
    const max = Math.max(...data, 1);
    return (
        <div className="flex flex-none flex-col" style={{ width: 96 }}>
            <span style={{ fontSize: 12, color: '#93A0AE' }}>최근 8주</span>
            <div className="flex items-end" style={{ height: 36, gap: 3, marginTop: 6 }}>
                {data.map((v, i) => (
                    <div
                        key={i}
                        style={{
                            flex: 1,
                            height: Math.max((v / max) * 36, 3),
                            borderRadius: 2,
                            background: i >= data.length - 3 ? (down ? '#FF5A64' : '#3D73DD') : '#DDE5F0',
                        }}
                    />
                ))}
            </div>
            <span style={{ fontSize: 11, color: '#B4BDC8', marginTop: 4 }}>
                {down ? '줄어드는 중' : '유지 중'}
            </span>
        </div>
    );
};

/**
 * 단계마다 "이 회원의 무엇이 문제인가"가 다르다.
 * 같은 숫자라도 첫 수강권 회원과 골든타임 회원에게 뜻하는 바가 달라서
 * 진단 문장을 단계 기준으로 만든다.
 */
const diagnose = (m: MemberView, stage: MemberStage): string => {
    // 모르는 값으로 문장을 만들면 "속도가 NaN% 줄었습니다"가 나온다.
    // 아는 것만 가지고 문장을 짓는다.
    const drop = m.paceDeltaPct;
    const gap = m.lastLessonDaysAgo;

    /*
     * 회차 개념이 없는 수강권(무제한·대관)에는 **회차 기반 문장을 만들지 않는다.**
     *
     * 실제로 이런 문장이 화면에 있었다 —
     *   "잔여 999회가 3회에 가까워지면 상담을 준비하세요"
     * 999 는 회차가 아니라 "무제한" 을 적는 자리이고(sessionless.ts),
     * 그 문장은 영원히 오지 않을 시점을 말한다. 이런 회원에게 실제로 남은
     * 기준은 만료일 하나뿐이라, 회차 자리를 만료일로 대체하거나 생략한다.
     */
    const sessionless = isSessionless(m);

    if (stage === 'GOLDEN') {
        const parts = [
            !sessionless && m.remain != null ? `남은 수업이 ${m.remain}회` : null,
            m.expiryDday != null ? `만료까지 ${m.expiryDday}일` : null,
        ].filter(Boolean);
        return parts.length
            ? `${parts.join(', ')}입니다. 이 안에 재등록 이야기를 꺼내야 합니다.`
            : '재등록을 꺼낼 시점입니다.';
    }

    if (stage === 'AT_RISK') {
        const head =
            drop != null
                ? `마지막 수업이 ${gap}일 전이고 수업 속도가 ${Math.abs(drop)}% 줄었습니다.`
                : `마지막 수업이 ${gap}일 전입니다. 비교할 이전 기록이 없어 속도 변화는 알 수 없습니다.`;
        /*
         * 여기에 "노쇼도 N%로 센터 평균의 세 배입니다" 가 붙었다.
         * 대표 지시로 표시에서 노쇼율을 뺐으므로 이 문장도 함께 바꾼다 —
         * 지표 스트립에서 지운 숫자를 진단 문장이 계속 인용하면, 원장님은
         * 화면 어디에도 없는 근거를 읽게 되고 그 문장이 그대로 강사에게 나간다.
         *
         * 판정 근거가 아니라 참고 수치인 것은 그대로다. **아는 경우에만** 덧붙인다 —
         * null(못 쟀다)이나 키 없음(구버전 서버)에는 아무 말도 하지 않는다.
         */
        const sameDay = isSameDayAdjustAlert(m)
            ? ` 최근 60일 당일 취소·변경도 ${m.sameDayAdjustments}회입니다.`
            : '';
        return head + sameDay;
    }

    if (stage === 'FIRST_PASS') {
        if (drop != null && drop <= -20) {
            return `첫 수강권인데 수업 속도가 벌써 ${Math.abs(drop)}% 줄었습니다. 지금이 붙잡을 시점입니다.`;
        }
        /* 무제한은 잔여가 줄어들지 않는다 — 상담 시점을 말할 수 있는 건 만료일뿐이다 */
        if (sessionless) {
            return m.expiryDday != null && m.expiryDday >= 0
                ? `아직 페이스가 유지되고 있습니다. 회차 제한이 없는 수강권이라 만료(D-${m.expiryDday})에 맞춰 상담을 준비하세요.`
                : '아직 페이스가 유지되고 있습니다. 회차 제한이 없는 수강권이라 만료일에 맞춰 상담을 준비하세요.';
        }
        if (m.remain != null) {
            return `아직 페이스가 유지되고 있습니다. 잔여 ${m.remain}회가 3회에 가까워지면 상담을 준비하세요.`;
        }
        return '첫 수강권 구간입니다. 이탈률이 가장 높은 시기라 눈여겨봐 주세요.';
    }

    /*
     * 🔴 주의 — **무엇에 걸렸는지를 말한다.**
     *
     * 이 칸의 회원은 아직 잘못된 게 없다. 그래서 문장이 "연락하세요"로 끝나면
     * 안 된다 — 원장님이 멀쩡한 회원에게 이탈 방어 연락을 걸게 되고,
     * 그 경험이 한 번 쌓이면 명단 전체를 안 믿는다.
     * 걸린 항목을 그대로 적고, 할 일은 「보는 것」으로 남긴다.
     *
     * 이유는 판정과 **같은 함수**(watchReasons)에서 가져온다. 여기서 조건을
     * 다시 적으면 판정과 설명이 갈리고, 그러면 이유 없이 서 있는 회원이 생긴다.
     */
    if (stage === 'WATCH') {
        const reasons = watchReasons(m).map((r) => WATCH_REASON_TEXT[r]);
        return reasons.length > 0
            ? `${reasons.join(' · ')}. 아직 위험은 아니지만 눈에 두실 회원입니다.`
            : '안정 기준에 조금 못 미칩니다. 아직 위험은 아니니 지켜봐 주세요.';
    }

    if (drop == null) return '비교할 이전 기록이 아직 없습니다. 조금 더 지켜봐야 합니다.';
    return drop <= STABLE_RULE.paceDropPct
        ? `잘 다니던 회원인데 최근 ${Math.abs(drop)}% 줄었습니다. 이유를 한번 확인해 보세요.`
        : '페이스가 안정적입니다. 따로 챙기지 않아도 되는 회원입니다.';
};

const MemberRow = ({
    m,
    stage,
    first,
    daysAgo,
    onOrder,
    onGift,
    giftHidden = false,
    giftDisabled = false,
    giftDisabledReason,
    sentGift = null,
    sentOrder,
    locked,
    showSameDay,
    siblings = [],
    onOpenValue,
}: {
    m: MemberView;
    stage: MemberStage;
    first: boolean;
    /**
     * 이 회원의 **다른 활성 수강권** 카드들.
     *
     * 서버는 활성 수강권 한 건당 카드 한 장을 준다. 상세만 보고 있으면
     * "이 회원에게 걸린 게 이게 전부"로 읽히므로 나머지를 내역으로 적는다.
     *
     * ⚠️ 합계를 만들지 않는다 — 카드 금액에는 각각 "다음 수강권 예상 × 재등록률"이
     *    들어 있어 더하면 그 기대액이 두 번 더해지고, DEFEND 와 GAIN 은 성격이
     *    달라 한 숫자로 합치면 화면이 갈라 둔 두 축이 다시 섞인다.
     */
    siblings?: MemberView[];
    /** 서버가 당일 취소·변경을 보내는가. 구버전이면 칸 자체를 뺀다 (요약 줄과 같은 판정) */
    showSameDay: boolean;
    /** 며칠 전 데이터를 보고 있나. 지시 문구에 남긴다 */
    daysAgo: number;
    /**
     * 지시 **확인 창을 연다.** 여기서 바로 서버로 나가지 않는다.
     * task 는 화면이 보여준 판단 문장 그대로.
     */
    onOrder: (m: MemberView, task: string) => void;
    /**
     * 선물 **확인 창을 연다.** 여기서 바로 포인트가 나가지 않는다.
     * task 는 화면이 보여준 판단 문장 그대로 — 발송 사유로 실린다.
     */
    onGift: (m: MemberView, task: string) => void;
    /** 이 계정으로는 선물을 보낼 수 없다 — 버튼을 아예 그리지 않는다 */
    giftHidden?: boolean;
    /** 지난 날짜라 선물을 보낼 수 없다 */
    giftDisabled?: boolean;
    giftDisabledReason?: string;
    /**
     * 이번에 이 회원에게 보낸 선물 — **문장 그대로** 받는다.
     *
     * ⚠️ 새로고침하면 사라진다 (서버 발송 내역에 수신자 id 가 없다).
     *    이중 발송은 멱등키가 서버에서 막는다 — 이 줄은 표시일 뿐이다.
     * ⚠️ 상품 이름을 여기서 조립하지 않는다 (BriefingCard 의 같은 prop 주석 참고).
     */
    sentGift?: string | null;
    /**
     * 이 회원 건으로 이미 나가 있는 지시.
     * 성공 토스트는 사라지므로, 보냈다는 사실은 목록에 남아 있어야 한다.
     */
    sentOrder: OrderView | null;
    /** 확인 창에서 전송 중이라 잠겨 있는가 — 목록 전체가 함께 잠긴다 */
    locked: boolean;
    /**
     * 금액 배지를 눌렀다 — 이 수강권 한 건의 산출 근거를 연다 (QA31).
     *
     * 창은 **페이지가** 띄웁니다. 이 카드는 상세(2300) 안에 있어서, 여기서
     * 직접 띄우면 창 안의 창이 되어 Esc 한 번에 둘 다 닫힙니다.
     */
    onOpenValue?: (axis: 'PREPAID' | 'EXPECTED') => void;
}) => {
    const down = (m.paceDeltaPct ?? 0) < 0;
    const risky = stage === 'AT_RISK';
    const key = KEY_METRIC[stage];
    const firstPass = isFirstPass(m.passOrder);
    const needsFix = needsClassification(m.passOrder);
    /*
     * 지시를 보낼 수 있는가.
     * 서버는 (memberId, coachId) 가 실제 담당 관계인지 확인하고, 아니면 400 을 준다.
     * coachId 가 비어 있는 회원은 애초에 보낼 곳이 없으므로 화면에서 먼저 막는다.
     * 오늘의 브리핑의 canCreateOrder() 와 같은 판단이다.
     */
    const canOrder = !!m.coachId;

    return (
        <div
            style={{
                padding: '18px 26px',
                borderTop: first ? 'none' : '1px solid #F1F4F9',
                background: '#FFFFFF',
            }}
        >
            {/* 이름줄 */}
            <div className="flex items-center" style={{ gap: 9 }}>
                <span style={{ fontSize: 18, fontWeight: 700, color: '#16202E', letterSpacing: '-0.01em' }}>
                    {m.name}
                </span>
                {/*
                  순번 뱃지.
                  "1번째"만 붉게 칠한다 — 이탈률이 가장 높은 구간이라 그렇다.
                  (수치는 passOrder.CHURN_RATE_BY_ORDER 가 원천이다. 여기 숫자를 적지 마라 —
                   실측이 갱신되면 화면마다 다른 이탈률을 말하게 된다)
                  재등록으로 들어와 "2번째 이상"인 회원은 여기서 색이 빠져야 한다.
                  근거는 툴팁으로 붙인다. 왜 2번째인지 설명 없으면 원장이 못 믿는다.
                */}
                <span
                    className="rounded-[5px]"
                    title={`${m.passOrder.reason}\n${m.churnHint.text}`}
                    style={{
                        background: firstPass ? '#FFE8E9' : needsFix ? '#FEF3C7' : '#F1F4F9',
                        color: firstPass ? '#D92D3A' : needsFix ? '#B45309' : '#5B6B7F',
                        fontSize: 11.5,
                        fontWeight: 800,
                        padding: '3px 8px',
                        cursor: 'help',
                    }}
                >
                    {needsFix ? '⚠ ' : ''}
                    {m.passOrder.label} 수강권
                </span>
                <span style={{ fontSize: 14, color: '#7C8794' }}>
                    {m.coachName} 강사 · {m.product}
                </span>
                {/*
                  회원 단위 요약 (백엔드 3차 memberRollup).

                  🔴 **이 상세는 여전히 수강권 한 건을 말합니다.** 위 잔여·만료·금액은
                     전부 이 수강권의 값입니다. 이 줄만 회원 전체를 말하므로
                     「회원 전체」라고 명시합니다 — 안 적으면 위아래 숫자가
                     서로 안 맞는 것처럼 보입니다.
                */}
                {(() => {
                    const roll = summarizeRollup(m.memberRollup);
                    if (!roll) return null;
                    return (
                        <span
                            style={{ fontSize: 12.5, color: '#7C8794' }}
                            title={roll.partial ? ROLLUP_PARTIAL_NOTE : undefined}
                        >
                            {roll.detail}
                        </span>
                    );
                })()}

                {/*
                  🔴 배지가 **두 조각**이 됐다 (2026-08-31 정책 M-D3).

                  예전에는 한 조각이었고 이름이 단계에 따라 「이탈 리스크」·
                  「예상 추가 매출」·「지켜야 할 매출」로 바뀌었습니다. 그런데 그 안의
                  숫자는 단계마다 **구성이 달랐습니다** — 어떤 줄은 잔여분+기대,
                  어떤 줄은 기대만. 이름은 셋인데 뜻은 그보다 많았습니다.

                  이제 조각마다 뜻이 하나입니다. 이탈 위험·주의 줄은 기대가 0 이라
                  자연히 「미사용 선수금」 하나만 남습니다.
                */}
                {m.value ? (
                    <span className="ml-auto inline-flex items-center" style={{ gap: 6 }}>
                        {/*
                          🔴 금액 배지가 **눌러진다** (2026-09-01 QA31).
                             누르면 이 수강권 한 건의 산출 근거가 뜬다 —
                             「잔여 15회 × 55,000원 = 825,000원」.
                             칩 자체가 이미 눌러 보이므로 밑줄은 붙이지 않는다.
                        */}
                        {(m.value.prepaid ?? 0) > 0 && (
                            <ValueTrigger
                                label={`${m.name} 미사용 선수금 산출 근거 보기`}
                                onClick={() => onOpenValue?.('PREPAID')}
                                underline={false}
                                className="inline-flex items-center rounded-[8px]"
                                style={{
                                    background: risky ? '#FFF0F0' : '#F1F5FC',
                                    border: `1px solid ${risky ? '#FBD0D3' : '#D3E0F5'}`,
                                    color: risky ? '#D92D3A' : '#2C4C8C',
                                    fontSize: 14,
                                    fontWeight: 800,
                                    padding: '7px 12px',
                                    gap: 6,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {risky ? '🚨' : '🛡'} 미사용 선수금 {won(m.value.prepaid as number)}원
                            </ValueTrigger>
                        )}
                        {(m.value.expected ?? 0) > 0 && (
                            <ValueTrigger
                                label={`${m.name} 재등록 기대 산출 근거 보기`}
                                onClick={() => onOpenValue?.('EXPECTED')}
                                underline={false}
                                className="inline-flex items-center rounded-[8px]"
                                style={{
                                    background: '#EAF7F2',
                                    border: '1px solid #B7DFD2',
                                    color: '#00795F',
                                    fontSize: 14,
                                    fontWeight: 800,
                                    padding: '7px 12px',
                                    gap: 6,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                💸 재등록 기대 {won(m.value.expected as number)}원
                            </ValueTrigger>
                        )}
                        {/*
                          🔴 둘 다 안 보일 때 **「0원」과 「모름」을 가른다** (교차검증 지적).
                             `prepaid` 가 null 이면 우리는 그 회원의 선수금을 **모른다** —
                             단가가 없는 수강권이라 못 셌다는 뜻이다. 그걸 「걸린 금액 0원」
                             이라고 적으면 원장님은 "다 쓴 회원"으로 읽는다. 정반대다.
                             0원이라고 말할 수 있는 것은 **0 으로 확인된 경우뿐**이다.
                        */}
                        {(m.value.prepaid ?? 0) <= 0 && (m.value.expected ?? 0) <= 0 && (
                            <span
                                title={
                                    m.value.prepaid == null
                                        ? `${m.value.breakdown}\n\n${VALUE_UNAVAILABLE_REASON}`
                                        : m.value.breakdown
                                }
                                className="inline-flex items-center rounded-[8px]"
                                style={{
                                    background: '#F4F6F9',
                                    border: m.value.prepaid == null ? '1px dashed #C9D3E0' : '1px solid #E2E8F0',
                                    color: '#6B7887',
                                    fontSize: 13,
                                    fontWeight: 700,
                                    padding: '7px 12px',
                                    cursor: 'help',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {m.value.prepaid == null ? VALUE_UNAVAILABLE_TEXT : '미사용 선수금 0원'}
                            </span>
                        )}
                    </span>
                ) : (
                    /*
                      금액을 모를 때 0원이라고 쓰면 "안 챙겨도 되는 회원"이 된다.
                      모른다고 그대로 적고, 왜 모르는지는 툴팁에 붙인다.
                    */
                    <span
                        className="ml-auto inline-flex items-center rounded-[8px]"
                        /* 왜 못 셌는지가 무제한 수강권과 무료권은 다르다 */
                        title={valueUnavailableReason(m)}
                        style={{
                            background: '#F4F6F9',
                            border: '1px dashed #C9D3E0',
                            color: '#6B7887',
                            fontSize: 13,
                            fontWeight: 700,
                            padding: '7px 12px',
                            gap: 6,
                            cursor: 'help',
                        }}
                    >
                        {VALUE_UNAVAILABLE_TEXT}
                    </span>
                )}
            </div>

            {/*
              이 회원의 다른 활성 수강권.

              위 배지는 **지금 보고 있는 수강권 한 건**의 금액이다. 회원 전체가 아니다.
              그 구분을 안 하면 원장님은 배지를 회원 총액으로 읽고, 목록에서 같은
              이름의 다른 줄을 보면 "숫자가 다르다"고 한다 — 실제로 그런 보고가 왔다.

              ⚠️ 여기서 **합계를 내지 않는다.** 선수금은 수강권마다 따로 받은 돈이라
                 더해도 되지만, **재등록 기대는 회원당 한 번**이다 (정책 M7).
                 카드마다 붙은 기대액을 다 더하면 그 회원이 다음에 수강권을 여러 번
                 사는 셈이 된다. 위쪽 헤더 합계는 그래서 대표 수강권 한 장에서만
                 기대를 센다(`pickPrimaryCards`). 여기서는 수강권별로 나란히 적기만 한다.
            */}
            {siblings.length > 0 && (
                <div
                    className="rounded-[9px]"
                    style={{
                        marginTop: 10,
                        background: '#FBFCFE',
                        border: `1px solid ${BORDER}`,
                        padding: '10px 13px',
                    }}
                >
                    <span style={{ fontSize: 12.5, color: '#5B6B7F', fontWeight: 700 }}>
                        이 회원은 활성 수강권이 {siblings.length + 1}건입니다 · 위 금액은 그중
                        「{m.product}」 한 건입니다
                    </span>
                    <div className="flex flex-col" style={{ gap: 4, marginTop: 7 }}>
                        {siblings.map((s) => (
                            <span
                                key={s.cardKey}
                                style={{ fontSize: 12.5, color: '#7C8794' }}
                                title={s.value ? s.value.breakdown : valueUnavailableReason(s)}
                            >
                                · {s.product} — {STAGE_META[s.stage].label} ·{' '}
                                {s.value
                                    ? [
                                          (s.value.prepaid ?? 0) > 0
                                              ? `선수금 ${won(s.value.prepaid as number)}원`
                                              : null,
                                          (s.value.expected ?? 0) > 0
                                              ? `기대 ${won(s.value.expected as number)}원`
                                              : null,
                                      ]
                                          .filter(Boolean)
                                          .join(' · ') ||
                                      /* 모르는 것을 0원으로 적지 않는다 — 위 배지와 같은 규칙 */
                                      (s.value.prepaid == null
                                          ? VALUE_UNAVAILABLE_TEXT
                                          : '미사용 선수금 0원')
                                    : VALUE_UNAVAILABLE_TEXT}
                            </span>
                        ))}
                    </div>
                    <span
                        style={{ display: 'block', marginTop: 7, fontSize: 11.5, color: '#A7B0BC' }}
                    >
                        ※ 선수금은 수강권마다 따로 받은 돈이라 더할 수 있지만, 재등록 기대는
                        회원당 한 번입니다. 그래서 여기에는 합계를 적지 않습니다.
                    </span>
                </div>
            )}

            {/* 한 줄 진단 — 숫자를 읽는 법 */}
            <div
                className="rounded-[9px]"
                style={{
                    marginTop: 10,
                    background: '#F7F9FC',
                    borderLeft: `3px solid ${STAGE_META[stage].dot}`,
                    padding: '10px 13px',
                }}
            >
                <span style={{ fontSize: 14.5, color: '#2C3A4B', lineHeight: '21px' }}>
                    {diagnose(m, stage)}
                </span>
            </div>

            {/* 지표 — 핵심 하나를 크게, 나머지는 보조로 */}
            <div
                className="flex items-stretch rounded-[11px]"
                style={{
                    marginTop: 12,
                    border: `1px solid ${BORDER}`,
                    background: '#FCFDFE',
                    padding: '10px 14px',
                    gap: 6,
                }}
            >
                <Spark data={m.trend} down={down} />

                <span style={{ width: 1, background: '#EDF1F7', flex: 'none', margin: '4px 8px' }} />

                <Cell
                    label="주당 수업"
                    /*
                      서버 paceNow 는 60일 창의 총 수업 수다. 그대로 찍으면
                      "주당 수업 10회"가 된다 — 실제로는 주 1.2회다.
                      환산은 pace.ts 한 곳에서만 하고, 원 수치는 툴팁에 병기한다.
                    */
                    value={weeklyPaceText(m.paceNow)}
                    unit="회"
                    hint={
                        m.paceDeltaPct == null
                            ? '비교할 이전 기록 없음'
                            : `이전 주 ${weeklyPaceText(m.paceBefore)}회 → ${down ? '▼' : '▲'} ${Math.abs(m.paceDeltaPct)}%`
                    }
                    title={paceTooltip(m.paceBefore, m.paceNow)}
                    /*
                      임계값은 memberSignals 한 곳에만 둔다.
                      요약 줄과 여기가 다른 기준을 쓰면, 목록에서 회색이던 회원을
                      열었더니 상세가 빨간 일이 생긴다.
                    */
                    alert={isPaceAlert(m)}
                    primary={key === '주당 수업'}
                />
                {/*
                  남은 수업.
                  무제한(대관)은 등록상 999회다 — 그대로 찍으면 실제 잔여로 읽힌다.
                  상담 시점 안내도 회차가 아니라 만료일이 말한다.
                */}
                <Cell
                    label="남은 수업"
                    value={
                        isSessionless(m)
                            ? SESSIONLESS_REMAIN_TEXT
                            : m.remain == null
                              ? '—'
                              : String(m.remain)
                    }
                    unit={isSessionless(m) || m.remain == null ? '' : '회'}
                    hint={
                        isSessionless(m)
                            ? SESSIONLESS_REMAIN_HINT
                            : m.remain == null
                              ? '회차 정보 없음'
                              : m.remain <= 3
                                ? '상담 시점입니다'
                                : '아직 여유 있음'
                    }
                    alert={isRemainAlert(m)}
                    primary={key === '남은 수업'}
                />
                <Cell
                    label="만료까지"
                    value={m.expiryDday == null ? '—' : String(m.expiryDday)}
                    unit={m.expiryDday == null ? '' : '일'}
                    hint={
                        m.expiryDday == null
                            ? '만료일 없음'
                            : m.expiryDday < 0
                              ? '이미 만료됨'
                              : m.expiryDday <= 14
                                ? '2주 안입니다'
                                : '여유 있음'
                    }
                    alert={isExpiryAlert(m)}
                />
                <Cell
                    label="마지막 수업"
                    // 0 으로 채우면 '오늘 수업함'이 되어 위험이 통째로 가려진다
                    value={m.lastLessonDaysAgo == null ? '기록 없음' : String(m.lastLessonDaysAgo)}
                    unit={m.lastLessonDaysAgo == null ? '' : '일 전'}
                    hint={m.lastLessonDaysAgo == null ? '아직 수업한 적 없음' : gapHint(m.lastLessonDaysAgo)}
                    // 이탈 위험·골든타임 구간에서는 일주일만 비어도 이미 늦다.
                    // 안정 구간과 같은 기준(2주)을 쓰면 정작 급한 회원이 안전해 보인다.
                    // (기준값은 memberSignals.gapAlertDays 한 곳에 있다)
                    alert={isGapAlert(m, stage)}
                    primary={key === '마지막 수업'}
                />
                {/*
                  당일 취소·변경.

                  이 자리에 노쇼율이 있었다 — 대표 지시로 **표시에서 뺐다.**
                  서버는 noshowPct 를 그대로 보내고 다른 화면·근거 모달이 여전히 쓴다.
                  여기서만 안 그린다.

                  요약 줄(MemberSummaryRow)과 **같은 세 갈래**를 지킨다 —
                  숫자 / '—'(못 쟀다) / 칸 없음(구버전 서버).
                  목록에서 '—' 이던 값이 열었더니 0회로 보이면 안 된다.
                */}
                {showSameDay && (
                    <Cell
                        label="당일 취소·변경"
                        value={m.sameDayAdjustments == null ? '—' : String(m.sameDayAdjustments)}
                        unit={m.sameDayAdjustments == null ? '' : '회'}
                        hint={
                            m.sameDayAdjustments == null
                                ? '세지 못했습니다'
                                : m.sameDayAdjustments >= SAME_DAY_ADJUST_ALERT
                                  ? '최근 60일 · 잦습니다'
                                  : '최근 60일'
                        }
                        title={
                            m.sameDayAdjustments == null
                                ? '최근 60일 당일 취소·변경을 세지 못했습니다. 0회라는 뜻이 아닙니다.'
                                : `최근 60일 당일 취소·변경 ${m.sameDayAdjustments}회 · ${SAME_DAY_ADJUST_ALERT}회부터 표시합니다`
                        }
                        alert={isSameDayAdjustAlert(m)}
                    />
                )}
            </div>

            {/* 액션 */}
            <div className="flex flex-col" style={{ marginTop: 12, gap: 6 }}>
                {/*
                  이미 지시한 건이 있으면 버튼 위에서 먼저 말한다.
                  서버는 중복을 막지 않는다 — 인덱스가 unique 가 아니다.
                  여기서 안 알리면 같은 회원 건이 강사 화면에 두 줄로 쌓인다.
                */}
                {sentOrder && (
                    <div
                        className="rounded-[9px]"
                        style={{
                            background: '#F4FCF9',
                            border: '1px solid #B7DFD2',
                            padding: '9px 12px',
                        }}
                    >
                        <span style={{ fontSize: 13, color: '#00795F', lineHeight: '19px' }}>
                            ✓ {sentOrder.sentLabel}에 {sentOrder.coachName} 강사에게 지시하셨습니다 · “
                            {sentOrder.task}”
                        </span>
                    </div>
                )}
                {sentGift && (
                    <div
                        className="rounded-[9px]"
                        style={{
                            background: '#FFFBEB',
                            border: '1px solid #FDE68A',
                            padding: '9px 12px',
                        }}
                    >
                        <span style={{ fontSize: 13, color: '#92400E', lineHeight: '19px' }}>
                            🎁 오늘 이 회원에게 {sentGift}
                        </span>
                    </div>
                )}
                <div className="flex items-center" style={{ gap: 8 }}>
                    <button
                        type="button"
                        // 화면에 보여준 진단 문장을 그대로 지시로 싣는다 (handleOrder 주석 참고)
                        onClick={() => onOrder(m, diagnose(m, stage))}
                        // 담당 강사가 없으면 보낼 곳이 없고, 다른 건 전송 중이면 잠근다
                        disabled={!canOrder || locked}
                        aria-disabled={!canOrder || locked}
                        title={
                            canOrder
                                ? undefined
                                : '담당 강사가 지정되지 않은 회원이라 지시를 보낼 수 없습니다. 회원 정보에서 담당 강사를 먼저 지정해 주세요.'
                        }
                        className="rounded-[10px]"
                        style={{
                            flex: 1,
                            height: 46,
                            background: !canOrder || locked ? '#E7EDF6' : '#1A305D',
                            border: 'none',
                            color: !canOrder || locked ? '#93A0AE' : '#FFFFFF',
                            fontSize: 15.5,
                            fontWeight: 700,
                            cursor: !canOrder ? 'not-allowed' : locked ? 'progress' : 'pointer',
                        }}
                    >
                        {!canOrder
                            ? '담당 강사 없음 · 지시 불가'
                            : sentOrder
                              ? `${m.coachName} 강사에게 다시 지시하기`
                              : `${m.coachName} 강사에게 지시하기`}
                    </button>
                    {/*
                      선물 보내기.

                      한때 "선물을 발송했습니다" 토스트만 띄우고 아무것도 보내지 않았다.
                      지금은 확인 창을 지나 실제로 나간다 — **포인트가 차감된다.**

                      그래서 다른 버튼과 조건이 다르다.
                        · 보낼 수 없는 계정(센터 열람·슈퍼관리자)에서는 아예 그리지 않는다
                        · 지난 날짜를 보고 있으면 잠근다 (확인 창도 열리지 않는다)
                      두 검사 모두 발송 직전에 한 번 더 한다 (useGiftSendFlow).
                    */}
                    {!giftHidden && (
                        <button
                            type="button"
                            onClick={() => onGift(m, diagnose(m, stage))}
                            disabled={giftDisabled || locked}
                            aria-disabled={giftDisabled || locked}
                            title={giftDisabled ? giftDisabledReason : undefined}
                            className="rounded-[10px]"
                            style={{
                                width: 168,
                                height: 46,
                                background: '#FFFFFF',
                                border: `1px solid ${giftDisabled || locked ? '#E2E8F0' : '#C7D3E4'}`,
                                color: giftDisabled || locked ? '#93A0AE' : '#1A305D',
                                fontSize: 14,
                                fontWeight: 700,
                                cursor: giftDisabled ? 'not-allowed' : locked ? 'progress' : 'pointer',
                            }}
                        >
                            🎁 선물 보내기
                        </button>
                    )}
                </div>
                {/*
                  왜 못 누르는지를 버튼 옆에서 밝힌다.
                  회색 버튼만 두면 원장님은 화면이 고장 났다고 생각한다.
                */}
                {!canOrder && (
                    <span style={{ fontSize: 12.5, color: '#B45309' }}>
                        담당 강사가 지정되지 않아 지시를 보낼 수 없습니다. 회원 정보에서 담당 강사를 먼저
                        지정해 주세요.
                    </span>
                )}
                {daysAgo > 0 && canOrder && (
                    <span style={{ fontSize: 12.5, color: '#93A0AE' }}>
                        {daysAgo}일 전 기록을 보고 계십니다. 지시에도 그대로 기록됩니다.
                    </span>
                )}
            </div>
        </div>
    );
};

export default OpsBriefingMembersPage;
